import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import axios from 'axios'
import { toast } from 'react-toastify'
import { PulseLoader } from 'react-spinners'
import { normalizePhoneNumber } from '../utils/phoneNumber'
import DueTimePicker from '../Components/DueTimePicker'
import { createPortal } from 'react-dom'
import {
  putFileToPresignedUrl,
  resolveFileContentType,
} from '../utils/s3Upload'

const API_URL = process.env.REACT_APP_API_URL

const CURRENCY_OPTIONS = ['INR', 'USD', 'EUR']
const DEFAULT_RFQ_STATUS = 'open'
const FINAL_STATUS = 'Active'
const DEFAULT_RFQ_DUE_TIME = '20:00'

const emptyMaterial = () => ({
  materialCode: '',
  materialDescription: '',
  hsn: '',
  uom: '',
  quantity: '',
  prNumber: '',
  pr_id: '',
  clientReference: '',
  lineNo: '',
  isBoq: false,
  boq: []
})

const getBoqId = (item) =>
  String(item?.boq_id || item?._id || item?.id || '').trim()

const emptyBoqItem = (parentLineNo, boqIndex, prNo = '') => ({
  boq_id: '',
  _id: '',
  boqNo: `${parentLineNo}.${boqIndex + 1}`,
  materialCode: '',
  materialDescription: '',
  hsnCode: '',
  uom: '',
  quantity: '',
  prNo: prNo || '',
  quotationStatus: ''
})

const renumberBoqItems = (boqList, parentLineNo) =>
  (Array.isArray(boqList) ? boqList : []).map((item, index) => ({
    ...item,
    boqNo: `${parentLineNo}.${index + 1}`
  }))

const mapBoqItems = (boqList) => {
  if (!Array.isArray(boqList)) return []
  return boqList.map((item) => {
    const boqId = getBoqId(item)
    return {
      boq_id: boqId,
      _id: boqId,
      boqNo: item?.boqNo || '',
      materialCode: item?.materialCode || '',
      materialDescription: item?.materialDescription || '',
      hsnCode: item?.hsnCode || item?.hsn || '',
      uom: item?.uom || '',
      quantity:
        item?.quantity != null && item?.quantity !== ''
          ? String(item.quantity)
          : '',
      prNo: item?.prNo || '',
      quotationStatus: item?.quotationStatus || ''
    }
  })
}

const emptyTerm = () => ({
  rfqTerm: '',
  termsResponse: ''
})

const DEFAULT_RFQ_TERMS = [
  {
    rfqTerm: 'Payment Terms',
    termsResponse: '30 days from the receipt of material'
  },
  {
    rfqTerm: 'Freight Terms',
    termsResponse: 'Free delivery to InstaTenders warehouse - Kundrathur, Chennai'
  },
  {
    rfqTerm: 'Delivery Location',
    termsResponse: 'InstaTenders Warehouse - Kundrathur, Chennai'
  },
  {
    rfqTerm: 'Lead Times',
    termsResponse: 'Please specify if any'
  },
  {
    rfqTerm: 'Warranty Terms',
    termsResponse: 'Please specify if any'
  },
  {
    rfqTerm: 'Special Instructions',
    termsResponse: 'Please specify if any'
  }
]

const getDefaultTerms = () =>
  DEFAULT_RFQ_TERMS.map((term) => ({
    rfqTerm: term.rfqTerm,
    termsResponse: term.termsResponse
  }))

const emptySupplier = () => ({
  supplierName: '',
  supplierMailId: '',
  phoneNumber: '',
  emailLink: '',
  contactPerson:'',
  address:'',
  gstNumber:'',
  gstStatus:'',
  gstLegalName:'',
  gstTradeName:'',
  gstConstitutionType:'',
  gstTaxPayerType:'',
  gstRegistrationDate:'',
  gstState:'',
  city:'',
  state:''

})

function parseAdditionalCcEmails(value) {
  return String(value || '')
    .split(/[,;]+/)
    .map((email) => email.trim())
    .filter(Boolean)
}

function isValidEmailAddress(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim())
}

function buildCcEmails(userEmail, additionalInput) {
  const result = []
  const seen = new Set()
  const addEmail = (email) => {
    const trimmed = String(email || '').trim()
    if (!trimmed) return
    const key = trimmed.toLowerCase()
    if (seen.has(key)) return
    seen.add(key)
    result.push(trimmed)
  }

  addEmail(userEmail)
  parseAdditionalCcEmails(additionalInput).forEach(addEmail)
  return result
}

function mapSupplierFromApi(supplier) {
  return {
    supplierId: supplier?.supplierId || '',
    supplierName: supplier?.supplierName || '',
    supplierMailId: supplier?.supplierMailId || '',
    phoneNumber: supplier?.phoneNumber || supplier?.phonenumber || '',
    emailLink: supplier?.emailLink || supplier?.quoteLink || '',
    contactPerson: supplier?.contactPerson || '',
    address: supplier?.address || '',
    gstNumber: supplier?.gstNumber || '',
    gstStatus: supplier?.gstStatus || '',
    gstLegalName: supplier?.gstLegalName || '',
    gstTradeName: supplier?.gstTradeName || '',
    gstConstitutionType: supplier?.gstConstitutionType || '',
    gstTaxPayerType: supplier?.gstTaxPayerType || '',
    gstRegistrationDate: supplier?.gstRegistrationDate || '',
    gstState: supplier?.gstState || '',
    city: supplier?.city || '',
    state: supplier?.state || ''
  }
}

function supplierAlreadyEmailed(supplier) {
  return Boolean(String(supplier?.emailLink || '').trim())
}

const getProcurementKey = (item, index) =>
  item._id ||
  `${item.prNo || ''}|${item.materialCode || ''}|${item.materialDescription || ''}|${index}`

const BOQ_SEL_SEP = '__boq__'

const getBoqSelectionKey = (parentKey, boqItem, boqIndex) =>
  `${parentKey}${BOQ_SEL_SEP}${getBoqId(boqItem) || boqIndex}`

const parseBoqSelectionKey = (key) => {
  const raw = String(key || '')
  const idx = raw.indexOf(BOQ_SEL_SEP)
  if (idx <= 0) return null
  return {
    parentKey: raw.slice(0, idx),
    boqId: raw.slice(idx + BOQ_SEL_SEP.length)
  }
}

const isEmptyMaterialRow = (row) =>
  !String(row.materialCode || '').trim() &&
  !String(row.materialDescription || '').trim() &&
  !String(row.hsn || '').trim() &&
  !String(row.uom || '').trim() &&
  !String(row.quantity || '').trim() 

function normalizePrId(value) {
  if (value == null || value === '') return ''
  if (typeof value === 'object') {
    if (value.$oid) return String(value.$oid).trim()
    if (value._id) return String(value._id).trim()
    if (value.id) return String(value.id).trim()
  }
  return String(value).trim()
}

function mapMaterialFromApi(m) {
  return {
    materialCode: m.materialCode || '',
    materialDescription: m.materialDescription || '',
    hsn: m.hsn || m.hsnCode || '',
    uom: m.uom || '',
    quantity: m.quantity != null ? String(m.quantity) : '',
    prNumber: m.prNumber || m.prNo || '',
    pr_id: normalizePrId(m.pr_id || m.prId),
    clientReference: m.clientReference || '',
    lineNo: m.lineNo != null ? m.lineNo : '',
    isBoq: !!m.isBoq,
    boq: mapBoqItems(m.boq)
  }
}

function materialMatchesProcurement(material, procurement) {
  if (!material || isEmptyMaterialRow(material)) return false

  const matPrId = String(material.pr_id || '').trim()
  const procId = String(
    procurement._id || procurement.pr_id || procurement.id || ''
  ).trim()
  if (matPrId && procId && matPrId === procId) return true

  const matPr = String(material.prNumber || '').trim()
  const procPr = String(procurement.prNo || procurement.prNumber || '').trim()
  const matCode = String(material.materialCode || '').trim()
  const procCode = String(procurement.materialCode || '').trim()
  const matDesc = String(material.materialDescription || '').trim()
  const procDesc = String(procurement.materialDescription || '').trim()

  if (!matCode && !matDesc && !matPr) return false

  return matPr === procPr && matCode === procCode && matDesc === procDesc
}

function isProcurementInMaterials(materials, procurement) {
  return materials.some((material) => materialMatchesProcurement(material, procurement))
}

function findMaterialRowForProcurement(materials, procurement) {
  return materials.find((material) => materialMatchesProcurement(material, procurement))
}

function isBoqItemInMaterials(materials, procurement, boqItem) {
  const parent = findMaterialRowForProcurement(materials, procurement)
  if (!parent || !Array.isArray(parent.boq) || parent.boq.length === 0) return false
  const boqId = getBoqId(boqItem)
  if (boqId) {
    return parent.boq.some((b) => getBoqId(b) === boqId)
  }
  const code = String(boqItem?.materialCode || '').trim()
  const boqNo = String(boqItem?.boqNo || '').trim()
  return parent.boq.some(
    (b) =>
      String(b.materialCode || '').trim() === code &&
      String(b.boqNo || '').trim() === boqNo
  )
}

const findNextMaterialWriteIndex = (rows) => {
  const emptyIndex = rows.findIndex(isEmptyMaterialRow)
  return emptyIndex === -1 ? rows.length : emptyIndex
}

function getProcurementQuotationStatus(item) {
  return String(item?.quotationStatus || '').trim()
}

function isQuotationStatusPending(item) {
  return getProcurementQuotationStatus(item).toLowerCase() === 'pending'
}

function isProcurementFrozen(item) {
  // Only Pending lines can be selected; any other status is locked
  return !isQuotationStatusPending(item)
}

function isBoqItemFrozen(boqItem) {
  return !isQuotationStatusPending(boqItem)
}

function parseApiResponse(raw) {
  let d = raw
  if (typeof d === 'string') {
    try {
      d = JSON.parse(d)
    } catch {
      return raw
    }
  }
  if (d?.body != null) {
    if (typeof d.body === 'string') {
      try {
        return JSON.parse(d.body)
      } catch {
        return d.body
      }
    }
    return d.body
  }
  return d
}

function isApiSuccess(responseData) {
  if (!responseData || typeof responseData !== 'object') return false
  const s = responseData.success
  return s === true || s === 'true' || s === 1 || s === '1'
}

function mapRfqOwnerFromApi(item) {
  return {
    s3Key: item?.s3Key || '',
    originalFileName: item?.originalFileName || '',
    contentType: item?.contentType || '',
    uploadedAt: item?.uploadedAt || '',
    url: item?.url || ''
  }
}

function formatFileSize(bytes) {
  if (bytes == null || Number.isNaN(Number(bytes))) return ''
  const size = Number(bytes)
  if (size < 1024) return `${size} B`
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`
  return `${(size / (1024 * 1024)).toFixed(1)} MB`
}

const MAX_RFQ_OWNER_ATTACHMENTS = 10

function formatDateForApi(isoDate) {
  if (!isoDate) return ''
  const [yyyy, mm, dd] = isoDate.split('-')
  if (!yyyy || !mm || !dd) return ''
  return `${dd}-${mm}-${yyyy}`
}

function formatDateTimeForApi(isoDate, timeHHmm) {
  if (!isoDate) return ''
  const [yyyy, mm, dd] = isoDate.split('-')
  if (!yyyy || !mm || !dd) return ''
  const time = String(timeHHmm || DEFAULT_RFQ_DUE_TIME).trim().slice(0, 5)
  return `${dd}-${mm}-${yyyy} ${time}`
}

function parseTime24(hhmm) {
  if (!hhmm) return { hour: '', minute: '', period: '' }
  const [hStr, mStr] = String(hhmm).trim().split(':')
  let h = parseInt(hStr, 10)
  const minute = String(mStr || '00')
    .padStart(2, '0')
    .slice(0, 2)
  if (Number.isNaN(h)) return { hour: '', minute: '', period: '' }

  const period = h >= 12 ? 'PM' : 'AM'
  if (h === 0) h = 12
  else if (h > 12) h -= 12

  return { hour: String(h), minute, period }
}

function formatTime12Preview(hhmm) {
  const { hour, minute, period } = parseTime24(hhmm)
  if (!hour || !minute || !period) return ''
  return `${hour}:${minute} ${period}`
}

const RFQ_DUE_DATE_OFFSET_DAYS = 2

function getTodayIsoDate() {
  const now = new Date()
  const pad = (n) => String(n).padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

function addDaysToIsoDate(isoDate, days) {
  if (!isoDate) return ''
  const [yyyy, mm, dd] = isoDate.split('-').map(Number)
  if (!yyyy || !mm || !dd) return ''
  const date = new Date(yyyy, mm - 1, dd)
  if (Number.isNaN(date.getTime())) return ''
  date.setDate(date.getDate() + days)
  const pad = (n) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

function parseDateFromApi(value) {
  if (!value) return ''
  const str = String(value).trim()
  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) {
    const [dd, mm, yyyy] = str.split('-')
    return `${yyyy}-${mm}-${dd}`
  }
  const dateTimeMatch = str.match(/^(\d{2})-(\d{2})-(\d{4})[ T]/)
  if (dateTimeMatch) {
    const [, dd, mm, yyyy] = dateTimeMatch
    return `${yyyy}-${mm}-${dd}`
  }
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  return d.toISOString().slice(0, 10)
}

function parseTimeFromApi(value) {
  if (!value) return ''
  const str = String(value).trim()
  const dateTimeMatch = str.match(/^\d{2}-\d{2}-\d{4}[ T](\d{2}):(\d{2})/)
  if (dateTimeMatch) {
    return `${dateTimeMatch[1]}:${dateTimeMatch[2]}`
  }
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n) => String(n).padStart(2, '0')
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function getDueDateTimeValue(rfqDueDate, rfqDueTime) {
  if (!rfqDueDate) return null
  const due = new Date(`${rfqDueDate}T${rfqDueTime || DEFAULT_RFQ_DUE_TIME}`)
  return Number.isNaN(due.getTime()) ? null : due
}

function isDueBeforeRfqDate(rfqDate, rfqDueDate, rfqDueTime) {
  if (!rfqDate || !rfqDueDate) return false
  const due = getDueDateTimeValue(rfqDueDate, rfqDueTime)
  const rfqStart = new Date(`${rfqDate}T00:00`)
  if (!due || Number.isNaN(rfqStart.getTime())) return false
  return due < rfqStart
}

function formatDueDateTimePreview(isoDate, timeHHmm) {
  if (!isoDate) return '-'
  const datePart = formatDateForApi(isoDate)
  const timePart = formatTime12Preview(timeHHmm)
  if (!datePart) return '-'
  return timePart ? `${datePart} ${timePart}` : datePart
}

function extractCreatedRfqId(responseData) {
  const data = responseData?.data
  if (!data) return responseData?.id || responseData?._id || null
  if (typeof data === 'string') return data
  return data._id || data.id || data.rfqId || null
}

const RfqCreate = () => {
  const navigate = useNavigate()
  const { id: editId } = useParams()
  const [activeTab, setActiveTab] = useState('details')
  const [isLoading, setIsLoading] = useState(false)
  const [isFetchingRfqNumber, setIsFetchingRfqNumber] = useState(!editId)
  const [isLoadingRfq, setIsLoadingRfq] = useState(Boolean(editId))
  const [rfqId, setRfqId] = useState(editId || null)

  const [rfqDetails, setRfqDetails] = useState(() => {
    const today = getTodayIsoDate()
    return {
      rfqNumber: '',
      rfqDate: today,
      rfqDueDate: addDaysToIsoDate(today, RFQ_DUE_DATE_OFFSET_DAYS),
      rfqDueTime: DEFAULT_RFQ_DUE_TIME,
      rfqSummary: '',
      rfqCurrency: 'INR',
      pdfLink: ''
    }
  })

  const [terms, setTerms] = useState(getDefaultTerms)
  const [materials, setMaterials] = useState([emptyMaterial()])
  const [activeMaterialRowIndex, setActiveMaterialRowIndex] = useState(0)
  const [prNoSearch, setPrNoSearch] = useState('')
  const [prSearchLoading, setPrSearchLoading] = useState(false)
  const [prSearchResults, setPrSearchResults] = useState([])
  const [selectedPrKeys, setSelectedPrKeys] = useState(new Set())
  const [prSearchResultsOpen, setPrSearchResultsOpen] = useState(false)

  const [supplierSearch, setSupplierSearch] = useState('')
  const [supplierSearchLoading, setSupplierSearchLoading] = useState(false)
  const [supplierSearchResults, setSupplierSearchResults] = useState([])
  const [suppliers, setSuppliers] = useState([emptySupplier()])
  const prSearchDebounceRef = useRef(null)
  const prSearchRequestIdRef = useRef(0)
  const supplierSearchDebounceRef = useRef(null)
  const supplierSearchRequestIdRef = useRef(0)

  const [rfqRecordStatus, setRfqRecordStatus] = useState('')
  const [isMailSent, setIsMailSent] = useState(false)
  const [userEmail, setUserEmail] = useState('')
  const [additionalCcEmails, setAdditionalCcEmails] = useState('')
  const [ccUserSuggestions, setCcUserSuggestions] = useState([])
  const [isSearchingCcUsers, setIsSearchingCcUsers] = useState(false)
  const ccSearchDebounceRef = useRef(null)
  const ccSearchRequestIdRef = useRef(0)
  const [isSendingEmail, setIsSendingEmail] = useState(false)
  const [isDeletingMaterial, setIsDeletingMaterial] = useState(false)
  const [materialDeleteIndex, setMaterialDeleteIndex] = useState(null)
  const [showPreviewStep, setShowPreviewStep] = useState(false)
  const [rfqOwnerAttachments, setRfqOwnerAttachments] = useState([])
  const [pendingAttachmentFiles, setPendingAttachmentFiles] = useState([])
  const [isUploadingAttachments, setIsUploadingAttachments] = useState(false)
  const attachmentInputRef = useRef(null)
  const [materialCatalog, setMaterialCatalog] = useState([])
  const [isMaterialsCatalogLoading, setIsMaterialsCatalogLoading] = useState(false)
  const [unitOptions, setUnitOptions] = useState([])
  const [isLoadingUnits, setIsLoadingUnits] = useState(false)
  const [materialSearchTerms, setMaterialSearchTerms] = useState({})
  const [showMaterialDropdown, setShowMaterialDropdown] = useState({})
  const [selectedMaterialIndex, setSelectedMaterialIndex] = useState({})
  const [materialDropdownPos, setMaterialDropdownPos] = useState({})
  const materialDescInputRefs = useRef({})                    
  const [showMaterialDescDropdown, setShowMaterialDescDropdown] = useState({})   
  const [selectedMaterialDescIndex, setSelectedMaterialDescIndex] = useState({})  
  const [materialDescDropdownPos, setMaterialDescDropdownPos] = useState({}) 

const materialInputRefs = useRef({})

  const isRfqActive =
    String(rfqRecordStatus || '').toLowerCase() === FINAL_STATUS.toLowerCase()

  const canAccessPreview = isRfqActive || showPreviewStep

  const loadRfqForEdit = useCallback(async () => {
    if (!editId) return

    setIsLoadingRfq(true)
    try {
      const response = await axios.post(API_URL, {
        action: 'get-rfq-by-id',
        rfqid: editId
      })
      const responseData = parseApiResponse(response.data)
      const rfq = responseData?.data

      if (!isApiSuccess(responseData) || !rfq || typeof rfq !== 'object') {
        toast.error(responseData?.message || 'Failed to load RFQ', { autoClose: 2000 })
        navigate('/rfq')
        return
      }

      setRfqId(rfq._id || rfq.id || editId)
      setRfqRecordStatus(rfq.status || '')
      setIsMailSent(Boolean(rfq.isMailSent))
      setRfqDetails({
        rfqNumber: rfq.rfqNumber || rfq.rfqNo || '',
        rfqDate: parseDateFromApi(rfq.rfqDate),
        rfqDueDate: parseDateFromApi(rfq.rfqDueDate),
        rfqDueTime: parseTimeFromApi(rfq.rfqDueDate) || DEFAULT_RFQ_DUE_TIME,
        rfqSummary: rfq.rfqSummary || '',
        rfqCurrency: rfq.rfqCurrency || 'INR',
        pdfLink: rfq.pdfLink || ''
      })

      if (Array.isArray(rfq.terms) && rfq.terms.length > 0) {
        setTerms(
          rfq.terms.map((t) => ({
            rfqTerm: t.rfqTerm || '',
            termsResponse: t.termsResponse || ''
          }))
        )
      } else if (rfq.rfqTerms) {
        setTerms([{ rfqTerm: rfq.rfqTerms, termsResponse: rfq.termsResponse || '' }])
      } else {
        setTerms(getDefaultTerms())
      }

      if (Array.isArray(rfq.materials) && rfq.materials.length > 0) {
        setMaterials(rfq.materials.map(mapMaterialFromApi))
      } else {
        setMaterials([emptyMaterial()])
      }

      if (Array.isArray(rfq.rfqOwner) && rfq.rfqOwner.length > 0) {
        setRfqOwnerAttachments(rfq.rfqOwner.map(mapRfqOwnerFromApi))
      } else {
        setRfqOwnerAttachments([])
      }
      setPendingAttachmentFiles([])

      if (Array.isArray(rfq.suppliers) && rfq.suppliers.length > 0) {
        setSuppliers(rfq.suppliers.map(mapSupplierFromApi))
      } else if (rfq.supplierMailId) {
        setSuppliers([{ supplierName: '', supplierMailId: rfq.supplierMailId, phoneNumber: '', emailLink: '' }])
      } else {
        setSuppliers([emptySupplier()])
      }

      if (String(rfq.status || '').toLowerCase() === FINAL_STATUS.toLowerCase()) {
        setShowPreviewStep(true)
        setActiveTab('preview')
      }
    } catch (error) {
      console.error('Load RFQ error:', error)
      toast.error('Failed to load RFQ', { autoClose: 2000 })
      navigate('/rfq')
    } finally {
      setIsLoadingRfq(false)
    }
  }, [editId, navigate])

  const fetchNextRfqNumber = async () => {
    setIsFetchingRfqNumber(true)
    try {
      const response = await axios.post(API_URL, {
        action: 'get-next-rfq-number'
      })
      const responseData = parseApiResponse(response.data)

      if (isApiSuccess(responseData) && responseData.data?.nextSequenceNumber) {
        setRfqDetails((prev) => ({
          ...prev,
          rfqNumber: responseData.data.nextSequenceNumber
        }))
      } else {
        toast.error(responseData?.message || 'Failed to fetch RFQ number', {
          autoClose: 2000
        })
      }
    } catch (error) {
      console.error('Fetch next RFQ number error:', error)
      const errData = error.response?.data ? parseApiResponse(error.response.data) : null
      toast.error(
        errData?.message || 'Failed to fetch next RFQ number',
        { autoClose: 2000 }
      )
    } finally {
      setIsFetchingRfqNumber(false)
    }
  }

  useEffect(() => {
    if (editId) {
      loadRfqForEdit()
    } else {
      fetchNextRfqNumber()
    }
  }, [editId, loadRfqForEdit])

  useEffect(() => {
    const fetchUserEmail = async () => {
      const userId = localStorage.getItem('id')
      if (!userId) return
      try {
        const response = await axios.post(API_URL, {
          action: 'get-user',
          id: userId
        })
        const responseData = parseApiResponse(response.data)
        if (isApiSuccess(responseData) && responseData.user?.email) {
          setUserEmail(String(responseData.user.email).trim())
        }
      } catch (error) {
        console.error('Fetch user email error:', error)
      }
    }
    fetchUserEmail()
  }, [])

  useEffect(() => {
    if (!window.$) return undefined

    const $modal = window.$('#modalDeleteMaterial')
    const onHidden = () => {
      setMaterialDeleteIndex(null)
    }

    $modal.on('hidden.bs.modal', onHidden)
    return () => {
      $modal.off('hidden.bs.modal', onHidden)
    }
  }, [])

  const extractPdfLink = (responseData) => {
    const data = responseData?.data
    if (!data || typeof data !== 'object') return ''
    return data.pdfLink || data.rfq?.pdfLink || ''
  }

  const refreshRfqById = useCallback(async (options = {}) => {
    const { preserveActiveStatus = false } = options
    if (!rfqId) return null
    try {
      const response = await axios.post(API_URL, {
        action: 'get-rfq-by-id',
        rfqid: rfqId
      })
      const responseData = parseApiResponse(response.data)
      const rfq = responseData?.data
      if (!isApiSuccess(responseData) || !rfq || typeof rfq !== 'object') {
        return null
      }
      if (!preserveActiveStatus) {
        setRfqRecordStatus(rfq.status || '')
      }
      setIsMailSent(Boolean(rfq.isMailSent))
      if (rfq.pdfLink) {
        setRfqDetails((prev) => ({
          ...prev,
          pdfLink: rfq.pdfLink
        }))
      }
      if (Array.isArray(rfq.rfqOwner)) {
        setRfqOwnerAttachments(rfq.rfqOwner.map(mapRfqOwnerFromApi))
      }
   if (Array.isArray(rfq.suppliers) && rfq.suppliers.length > 0) {
  const apiByMail = new Map(
    rfq.suppliers.map((s) => [
      String(s.supplierMailId || '').trim().toLowerCase(),
      mapSupplierFromApi(s)
    ])
  )
  setSuppliers((prev) => {
       const next = prev
      .filter((row) => row.supplierName.trim() || row.supplierMailId.trim())
      .map((row) => {
        const mail = row.supplierMailId.trim().toLowerCase()
        const fromApi = apiByMail.get(mail)
        if (!fromApi) return row
        return {
          ...row,
          supplierId: fromApi.supplierId || row.supplierId || '',
          supplierName: fromApi.supplierName || row.supplierName,
          phoneNumber: fromApi.phoneNumber || row.phoneNumber || '',
          emailLink: fromApi.emailLink || row.emailLink || '',
          contactPerson: fromApi.contactPerson || row.contactPerson || '',
          address: fromApi.address || row.address || '',
          gstNumber: fromApi.gstNumber || row.gstNumber || '',
          gstStatus: fromApi.gstStatus || row.gstStatus || '',
          gstLegalName: fromApi.gstLegalName || row.gstLegalName || '',
          gstTradeName: fromApi.gstTradeName || row.gstTradeName || '',
          gstConstitutionType: fromApi.gstConstitutionType || row.gstConstitutionType || '',
          gstTaxPayerType: fromApi.gstTaxPayerType || row.gstTaxPayerType || '',
          gstRegistrationDate: fromApi.gstRegistrationDate || row.gstRegistrationDate || '',
          gstState: fromApi.gstState || row.gstState || '',
          city: fromApi.city || row.city || '',
          state: fromApi.state || row.state || ''
        }
      })
    apiByMail.forEach((apiRow, mail) => {
      const exists = next.some(
        (row) => row.supplierMailId.trim().toLowerCase() === mail
      )
      if (!exists) next.push(apiRow)
    })
    return next.length > 0 ? next : [emptySupplier()]
  })
}
      if (String(rfq.status || '').toLowerCase() === FINAL_STATUS.toLowerCase()) {
        setShowPreviewStep(true)
      }
      return rfq
    } catch (error) {
      console.error('Refresh RFQ error:', error)
      return null
    }
  }, [rfqId])

  const handleDetailsChange = (e) => {
    const { name, value } = e.target
    if (name === 'rfqNumber') return
    setRfqDetails((prev) => {
      const next = { ...prev, [name]: value }
      if (name === 'rfqDate') {
        next.rfqDueDate = value ? addDaysToIsoDate(value, RFQ_DUE_DATE_OFFSET_DAYS) : ''
      }
      return next
    })
  }

  const handleDueTimeChange = (rfqDueTime) => {
    setRfqDetails((prev) => ({ ...prev, rfqDueTime }))
  }

  const updateTerm = (index, field, value) => {
    setTerms((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)))
  }

  const addTermRowAfter = (index) => {
    setTerms((prev) => {
      const next = [...prev]
      next.splice(index + 1, 0, emptyTerm())
      return next
    })
  }

  const removeTermRow = (index) => {
    if (terms.length <= 1) {
      setTerms([emptyTerm()])
      return
    }
    setTerms((prev) => prev.filter((_, i) => i !== index))
  }

const updateSupplier = (index, field, value) => {
  setSuppliers((prev) =>
    prev.map((row, i) => {
      if (i !== index) return row
      const next = { ...row, [field]: value }
      if (field === 'supplierMailId') {
        next.emailLink = ''
        next.supplierId = ''
      }
      return next
    })
  )
}

  const addSupplierRowAfter = (index) => {
    setSuppliers((prev) => {
      const next = [...prev]
      next.splice(index + 1, 0, emptySupplier())
      return next
    })
  }

  const removeSupplierRow = (index) => {
    if (suppliers.length <= 1) {
      setSuppliers([emptySupplier()])
      return
    }
    setSuppliers((prev) => prev.filter((_, i) => i !== index))
  }

  const SUPPLIER_SEARCH_MIN_LENGTH = 3
  const SUPPLIER_SEARCH_DEBOUNCE_MS = 400

  const getAddedSupplierEmails = () =>
    new Set(
      suppliers
        .map((s) => s.supplierMailId.trim().toLowerCase())
        .filter(Boolean)
    )

  const isSupplierAlreadyAdded = (email) =>
    getAddedSupplierEmails().has(String(email || '').trim().toLowerCase())

  const searchSuppliers = useCallback(async (searchTerm, options = {}) => {
    const { showToast = true } = options
    const search = String(searchTerm ?? '').trim()

    if (!search) {
      setSupplierSearchResults([])
      if (showToast) {
        toast.error('Enter supplier name or email to search', { autoClose: 2000 })
      }
      return
    }

    if (search.length < SUPPLIER_SEARCH_MIN_LENGTH) {
      setSupplierSearchResults([])
      return
    }

    const requestId = ++supplierSearchRequestIdRef.current
    setSupplierSearchLoading(true)

    // Supplier picker for RFQ — lookup only; does not require supplier_master moduleAccess.
    try {
      const userRole = localStorage.getItem('role')
      const payload = {
        action: 'get-suppliers',
        status: 'active',
        search
      }
      if (userRole !== 'admin') {
        payload.status = 'active'
      }

      const response = await axios.post(API_URL, payload)

      if (requestId !== supplierSearchRequestIdRef.current) {
        return
      }

      const responseData = parseApiResponse(response.data)
      const list = responseData?.suppliers || responseData?.data?.suppliers || []

      if (isApiSuccess(responseData) && Array.isArray(list) && list.length > 0) {
        setSupplierSearchResults(list)
        if (showToast) {
          toast.info(`${list.length} supplier(s) found.`, { autoClose: 2500 })
        }
      } else {
        setSupplierSearchResults([])
        if (showToast) {
          toast.info(responseData?.message || 'No suppliers found', { autoClose: 2000 })
        }
      }
    } catch (error) {
      if (requestId !== supplierSearchRequestIdRef.current) {
        return
      }
      console.error('Supplier search error:', error)
      const errData = error.response?.data ? parseApiResponse(error.response.data) : null
      toast.error(errData?.message || 'Failed to search suppliers', { autoClose: 2000 })
      setSupplierSearchResults([])
    } finally {
      if (requestId === supplierSearchRequestIdRef.current) {
        setSupplierSearchLoading(false)
      }
    }
  }, [])

  const handleSupplierSearchChange = (e) => {
    const value = e.target.value
    setSupplierSearch(value)
    supplierSearchRequestIdRef.current += 1

    if (supplierSearchDebounceRef.current) {
      clearTimeout(supplierSearchDebounceRef.current)
    }

    const trimmed = value.trim()
    if (trimmed.length < SUPPLIER_SEARCH_MIN_LENGTH) {
      setSupplierSearchLoading(false)
      setSupplierSearchResults([])
      return
    }

    supplierSearchDebounceRef.current = setTimeout(() => {
      searchSuppliers(trimmed, { showToast: false })
    }, SUPPLIER_SEARCH_DEBOUNCE_MS)
  }

  const handleSupplierSearch = () => {
    if (supplierSearchDebounceRef.current) {
      clearTimeout(supplierSearchDebounceRef.current)
    }
    searchSuppliers(supplierSearch, { showToast: true })
  }

  const handleSupplierSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSupplierSearch()
    }
  }

  // --- Additional CC email autocomplete (RFQ user lookup; no user_management required) ---
  const getCcSearchTerm = (value) => {
    const segments = String(value || '').split(/[,;]/)
    return segments[segments.length - 1].trim()
  }

  const searchCcUsers = async (term, requestId) => {
    try {
      const response = await axios.post(API_URL, {
        action: 'get-users',
        page: 1,
        limit: 25,
        sortBy: 'createdAt',
        sortOrder: 'desc',
        search: term
      })
      if (requestId !== ccSearchRequestIdRef.current) return

      const data = parseApiResponse(response.data)
      const users = Array.isArray(data?.users) ? data.users : []
      setCcUserSuggestions(
        users.filter((user) => isValidEmailAddress(user?.email))
      )
    } catch (error) {
      console.error('Search CC users error:', error)
      if (requestId === ccSearchRequestIdRef.current) {
        setCcUserSuggestions([])
      }
    } finally {
      if (requestId === ccSearchRequestIdRef.current) {
        setIsSearchingCcUsers(false)
      }
    }
  }

  const handleAdditionalCcChange = (e) => {
    const value = e.target.value
    setAdditionalCcEmails(value)

    ccSearchRequestIdRef.current += 1
    const requestId = ccSearchRequestIdRef.current
    if (ccSearchDebounceRef.current) {
      clearTimeout(ccSearchDebounceRef.current)
    }

    const term = getCcSearchTerm(value)
    if (term.length < 3) {
      setCcUserSuggestions([])
      setIsSearchingCcUsers(false)
      return
    }

    setIsSearchingCcUsers(true)
    ccSearchDebounceRef.current = setTimeout(() => {
      searchCcUsers(term, requestId)
    }, 400)
  }

  const handleSelectCcUser = (email) => {
    const selected = String(email || '').trim()
    if (!selected) return

    setAdditionalCcEmails((prev) => {
      const value = String(prev || '')
      const lastComma = Math.max(value.lastIndexOf(','), value.lastIndexOf(';'))
      const base = lastComma >= 0 ? `${value.slice(0, lastComma + 1)} ` : ''
      const existing = parseAdditionalCcEmails(base)
      if (
        existing.some((item) => item.toLowerCase() === selected.toLowerCase())
      ) {
        return `${base.trim()} `.trimStart()
      }
      return `${base}${selected}, `
    })
    ccSearchRequestIdRef.current += 1
    setCcUserSuggestions([])
    setIsSearchingCcUsers(false)
  }

  const handleAdditionalCcBlur = () => {
    // Delay so suggestion clicks (onMouseDown) land first
    setTimeout(() => {
      setCcUserSuggestions([])
      setIsSearchingCcUsers(false)
    }, 180)
  }

 const selectSupplier = (supplier) => {
  const email = (supplier.email || '').trim()
  const name = (supplier.clientname || '').trim()
  const supplierId = String(supplier._id || supplier.id || '').trim()
  if (!email) {
    toast.error('This supplier has no email on record', { autoClose: 2000 })
    return
  }
  if (isSupplierAlreadyAdded(email)) {
    toast.info('Supplier already added', { autoClose: 2000 })
    return
  }
  setSuppliers((prev) => {
    const emptyIdx = prev.findIndex(
      (s) => !s.supplierName.trim() && !s.supplierMailId.trim()
    )
    const phone = String(supplier.phonenumber || supplier.phoneNumber || '').trim()
    const row = {
      supplierId,
      supplierName: name,
      supplierMailId: email,
      phoneNumber: phone,
      emailLink: '',
      contactPerson: String(supplier.contactPerson || '').trim(),
      address: String(supplier.address || '').trim(),
      gstNumber: String(supplier.gstNumber || '').trim(),
      gstStatus: String(supplier.gstStatus || '').trim(),
      gstLegalName: String(supplier.gstLegalName || '').trim(),
      gstTradeName: String(supplier.gstTradeName || '').trim(),
      gstConstitutionType: String(supplier.gstConstitutionType || '').trim(),
      gstTaxPayerType: String(supplier.gstTaxPayerType || '').trim(),
      gstRegistrationDate: String(supplier.gstRegistrationDate || '').trim(),
      gstState: String(supplier.gstState || '').trim(),
      city: String(supplier.city || '').trim(),
      state: String(supplier.state || '').trim()
    }
    if (emptyIdx === -1) return [...prev, row]
    const next = prev.map((s, i) => (i === emptyIdx ? row : s))
    const hasEmptyRow = next.some(
      (s) => !s.supplierName.trim() && !s.supplierMailId.trim()
    )
    return hasEmptyRow ? next : [...next, emptySupplier()]
  })
  toast.success('Supplier added', { autoClose: 1500 })
}

  const updateMaterial = (index, field, value) => {
    setMaterials((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    )
  }

  const handleQuantityChange = (index, value) => {
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      updateMaterial(index, 'quantity', value)
    }
  }

  const addMaterialRowAfter = (index) => {
    setMaterials((prev) => {
      const next = [...prev]
      next.splice(index + 1, 0, emptyMaterial())
      return next
    })
    setActiveMaterialRowIndex(index + 1)
  }

  const addBoqUnderMaterial = (parentIndex) => {
    setMaterials((prev) =>
      prev.map((row, i) => {
        if (i !== parentIndex) return row
        const lineNo = row.lineNo || parentIndex + 1
        const existing = Array.isArray(row.boq) ? row.boq : []
        const nextBoq = renumberBoqItems(
          [
            ...existing,
            emptyBoqItem(lineNo, existing.length, row.prNumber || '')
          ],
          lineNo
        )
        return { ...row, isBoq: true, boq: nextBoq }
      })
    )
    setActiveMaterialRowIndex(parentIndex)
  }

  const updateBoqField = (parentIndex, boqIndex, field, value) => {
    setMaterials((prev) =>
      prev.map((row, i) => {
        if (i !== parentIndex) return row
        const nextBoq = (Array.isArray(row.boq) ? row.boq : []).map(
          (item, bi) => (bi === boqIndex ? { ...item, [field]: value } : item)
        )
        return { ...row, boq: nextBoq }
      })
    )
  }

  const handleBoqQuantityChange = (parentIndex, boqIndex, value) => {
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      updateBoqField(parentIndex, boqIndex, 'quantity', value)
    }
  }

  const removeBoqUnderMaterial = (parentIndex, boqIndex) => {
    setMaterials((prev) =>
      prev.map((row, i) => {
        if (i !== parentIndex) return row
        const lineNo = row.lineNo || parentIndex + 1
        const nextBoq = renumberBoqItems(
          (Array.isArray(row.boq) ? row.boq : []).filter(
            (_, bi) => bi !== boqIndex
          ),
          lineNo
        )
        return {
          ...row,
          isBoq: nextBoq.length > 0,
          boq: nextBoq
        }
      })
    )
  }

  const removeMaterialRowFromState = (index) => {
    setMaterials((prev) => {
      const next = prev.filter((_, i) => i !== index)
      return next.length > 0 ? next : [emptyMaterial()]
    })
    setActiveMaterialRowIndex((prev) => {
      if (prev === index) return Math.max(0, index - 1)
      if (prev > index) return prev - 1
      return prev
    })
  }

  const canRemoveMaterialRow = (index) => {
    if (materials.length > 1) return true
    const row = materials[index]
    return Boolean(rfqId && normalizePrId(row?.pr_id))
  }

  const getMaterialLabel = (row) =>
    row?.materialDescription?.trim() ||
    row?.materialCode?.trim() ||
    row?.prNumber?.trim() ||
    '-'

  const handleMaterialDeleteClick = (index) => {
    if (!canRemoveMaterialRow(index)) return
    setMaterialDeleteIndex(index)
    if (window.$) {
      window.$('#modalDeleteMaterial').modal('show')
    }
  }

  const closeMaterialDeleteModal = () => {
    if (window.$) {
      window.$('#modalDeleteMaterial').modal('hide')
    }
    setMaterialDeleteIndex(null)
  }

  const handleConfirmDeleteMaterial = async () => {
    if (materialDeleteIndex == null) return

    const index = materialDeleteIndex
    const row = materials[index]
    const prId = normalizePrId(row?.pr_id)

    if (rfqId && prId) {
      const userId = getUserId()
      if (!userId) return

      setIsDeletingMaterial(true)
      try {
        const response = await axios.post(API_URL, {
          action: 'delete-rfq-material',
          id: rfqId,
          pr_id: prId,
          userid: userId
        })
        const responseData = parseApiResponse(response.data)

        if (!isApiSuccess(responseData)) {
          toast.error(responseData?.message || 'Failed to delete material', {
            autoClose: 2000
          })
          return
        }

        const deleteData = responseData?.data
        if (deleteData?.removedFromRfq === false) {
          removeMaterialRowFromState(index)
        } else {
          const rfq = deleteData?.rfq
          const updatedMaterials = Array.isArray(deleteData?.materials)
            ? deleteData.materials
            : Array.isArray(rfq?.materials)
              ? rfq.materials
              : null

          if (Array.isArray(updatedMaterials)) {
            setMaterials(
              updatedMaterials.length > 0
                ? updatedMaterials.map(mapMaterialFromApi)
                : [emptyMaterial()]
            )
          } else {
            removeMaterialRowFromState(index)
          }
        }

        toast.success(responseData?.message || 'Material deleted', { autoClose: 1500 })
        closeMaterialDeleteModal()
      } catch (error) {
        console.error('Delete RFQ material error:', error)
        const errData = error.response?.data ? parseApiResponse(error.response.data) : null
        toast.error(errData?.message || 'Failed to delete material', { autoClose: 2000 })
      } finally {
        setIsDeletingMaterial(false)
      }
      return
    }

    removeMaterialRowFromState(index)
    closeMaterialDeleteModal()
  }

  const procurementToMaterialRow = (procurement) => ({
    materialCode: procurement.materialCode || '',
    materialDescription: procurement.materialDescription || '',
    hsn: procurement.hsnCode || procurement.hsn || '',
    uom: procurement.uom || '',
    quantity:
      procurement.quantity != null && procurement.quantity !== ''
        ? String(procurement.quantity)
        : '',
    prNumber: String(procurement.prNo || procurement.prNumber || '').trim(),
    pr_id: String(procurement._id || procurement.pr_id || procurement.id || '').trim(),
    clientReference: String(procurement.clientReference || '').trim(),
    lineNo: procurement.lineNo != null ? procurement.lineNo : '',
    isBoq: !!procurement.isBoq,
    boq: mapBoqItems(procurement.boq)
  })

  const getSelectablePrKeys = () => {
    const keys = []
    prSearchResults.forEach((item, index) => {
      const parentKey = getProcurementKey(item, index)
      const parentAdded = isProcurementInMaterials(materials, item)
      if (!parentAdded && !isProcurementFrozen(item)) {
        keys.push(parentKey)
      }
      if (item.isBoq && Array.isArray(item.boq)) {
        item.boq.forEach((boqItem, boqIndex) => {
          if (isBoqItemFrozen(boqItem)) return
          if (isBoqItemInMaterials(materials, item, boqItem)) return
          keys.push(getBoqSelectionKey(parentKey, boqItem, boqIndex))
        })
      }
    })
    return keys
  }

  const countPendingPrSelection = () => {
    let count = 0
    prSearchResults.forEach((item, index) => {
      const parentKey = getProcurementKey(item, index)
      if (
        selectedPrKeys.has(parentKey) &&
        !isProcurementInMaterials(materials, item)
      ) {
        count += 1
      }
      if (item.isBoq && Array.isArray(item.boq)) {
        item.boq.forEach((boqItem, boqIndex) => {
          const boqKey = getBoqSelectionKey(parentKey, boqItem, boqIndex)
          if (
            selectedPrKeys.has(boqKey) &&
            !isBoqItemInMaterials(materials, item, boqItem)
          ) {
            count += 1
          }
        })
      }
    })
    return count
  }

  const countAddedInCurrentResults = () => {
    let count = 0
    prSearchResults.forEach((item) => {
      if (isProcurementInMaterials(materials, item)) count += 1
      if (item.isBoq && Array.isArray(item.boq)) {
        item.boq.forEach((boqItem) => {
          if (isBoqItemInMaterials(materials, item, boqItem)) count += 1
        })
      }
    })
    return count
  }

  const getSelectableBoqKeysForParent = (item, parentKey) => {
    if (!item?.isBoq || !Array.isArray(item.boq)) return []
    return item.boq
      .map((boqItem, boqIndex) => ({
        boqItem,
        key: getBoqSelectionKey(parentKey, boqItem, boqIndex)
      }))
      .filter(
        ({ boqItem }) =>
          !isBoqItemFrozen(boqItem) &&
          !isBoqItemInMaterials(materials, item, boqItem)
      )
      .map(({ key }) => key)
  }

  const togglePrSelection = (key) => {
    const boqRef = parseBoqSelectionKey(key)
    if (boqRef) {
      const parentEntry = prSearchResults
        .map((item, index) => ({
          item,
          index,
          key: getProcurementKey(item, index)
        }))
        .find((entry) => entry.key === boqRef.parentKey)
      if (!parentEntry) return
      const boqItem = (parentEntry.item.boq || []).find(
        (b, bi) =>
          getBoqSelectionKey(boqRef.parentKey, b, bi) === key ||
          String(getBoqId(b) || bi) === String(boqRef.boqId)
      )
      if (!boqItem) return
      if (isBoqItemInMaterials(materials, parentEntry.item, boqItem)) return
      if (isBoqItemFrozen(boqItem)) return

      setSelectedPrKeys((prev) => {
        const next = new Set(prev)
        if (next.has(key)) {
          next.delete(key)
          // If any child is unchecked, uncheck parent too
          next.delete(boqRef.parentKey)
        } else {
          next.add(key)
        }
        return next
      })
      return
    }

    const parentEntry = prSearchResults
      .map((item, index) => ({
        item,
        index,
        key: getProcurementKey(item, index)
      }))
      .find((entry) => entry.key === key)
    if (!parentEntry) return
    if (isProcurementInMaterials(materials, parentEntry.item)) return
    if (isProcurementFrozen(parentEntry.item)) return

    const childKeys = getSelectableBoqKeysForParent(
      parentEntry.item,
      parentEntry.key
    )

    setSelectedPrKeys((prev) => {
      const next = new Set(prev)
      const selecting = !next.has(key)
      if (selecting) {
        // Parent select → auto-select all selectable BOQ children
        next.add(key)
        childKeys.forEach((boqKey) => next.add(boqKey))
      } else {
        // Parent deselect → clear all its BOQ children
        next.delete(key)
        childKeys.forEach((boqKey) => next.delete(boqKey))
        // Also clear any remaining boq keys for this parent
        Array.from(next).forEach((k) => {
          const ref = parseBoqSelectionKey(k)
          if (ref && ref.parentKey === key) next.delete(k)
        })
      }
      return next
    })
  }

  const toggleSelectAllPrResults = () => {
    const selectable = getSelectablePrKeys()
    if (selectable.length === 0) return

    const allSelectableSelected = selectable.every((key) => selectedPrKeys.has(key))
    setSelectedPrKeys((prev) => {
      const next = new Set(prev)
      if (allSelectableSelected) {
        selectable.forEach((key) => next.delete(key))
      } else {
        selectable.forEach((key) => next.add(key))
      }
      return next
    })
  }

  const applySelectedProcurements = () => {
    const selections = prSearchResults
      .map((item, index) => {
        const parentKey = getProcurementKey(item, index)
        const parentSelected =
          selectedPrKeys.has(parentKey) &&
          !isProcurementInMaterials(materials, item) &&
          !isProcurementFrozen(item)

        const selectedBoq = (item.isBoq && Array.isArray(item.boq) ? item.boq : [])
          .map((boqItem, boqIndex) => ({
            boqItem,
            boqIndex,
            key: getBoqSelectionKey(parentKey, boqItem, boqIndex)
          }))
          .filter(
            ({ boqItem, key }) =>
              selectedPrKeys.has(key) &&
              !isBoqItemFrozen(boqItem) &&
              !isBoqItemInMaterials(materials, item, boqItem)
          )

        return {
          item,
          parentKey,
          parentSelected,
          selectedBoq
        }
      })
      .filter(
        ({ parentSelected, selectedBoq }) =>
          parentSelected || selectedBoq.length > 0
      )

    if (selections.length === 0) {
      toast.error('Select new Pending items that are not already added', {
        autoClose: 2000
      })
      return
    }

    let addedCount = 0
    let nextActiveRow = 0

    setMaterials((prev) => {
      const next = [...prev]
      let writeIndex = findNextMaterialWriteIndex(next)

      selections.forEach(({ item, parentSelected, selectedBoq }) => {
        const existingIndex = next.findIndex((row) =>
          materialMatchesProcurement(row, item)
        )
        const selectedBoqMapped = selectedBoq.map(({ boqItem }) =>
          mapBoqItems([boqItem])[0]
        )

        if (existingIndex >= 0) {
          const existing = next[existingIndex]
          const mergedBoq = [...(existing.boq || [])]
          selectedBoqMapped.forEach((boqRow) => {
            const already = mergedBoq.some((b) => {
              const idA = getBoqId(boqRow)
              const idB = getBoqId(b)
              if (idA && idB && idA === idB) return true
              return (
                String(b.boqNo || '') === String(boqRow.boqNo || '') &&
                String(b.materialCode || '') ===
                  String(boqRow.materialCode || '')
              )
            })
            if (!already) {
              mergedBoq.push(boqRow)
              addedCount += 1
            }
          })
          next[existingIndex] = {
            ...existing,
            isBoq: mergedBoq.length > 0 ? true : !!existing.isBoq,
            boq: mergedBoq,
            lineNo: item.lineNo != null ? item.lineNo : existing.lineNo
          }
          return
        }

        if (parentSelected || selectedBoqMapped.length > 0) {
          const rowData = procurementToMaterialRow(item)
          // Parent select auto-selects children; keep only chosen BOQ lines
          if (selectedBoqMapped.length > 0) {
            rowData.isBoq = true
            rowData.boq = selectedBoqMapped
          } else {
            rowData.isBoq = false
            rowData.boq = []
          }
          if (writeIndex < next.length) {
            next[writeIndex] = rowData
          } else {
            next.push(rowData)
          }
          writeIndex += 1
          addedCount += 1 + selectedBoqMapped.length
        }
      })

      nextActiveRow = writeIndex
      return next
    })
    setActiveMaterialRowIndex(nextActiveRow)

    setSelectedPrKeys((prev) => {
      const next = new Set(prev)
      selections.forEach(({ parentKey, parentSelected, selectedBoq }) => {
        if (parentSelected) next.delete(parentKey)
        selectedBoq.forEach(({ key }) => next.delete(key))
      })
      return next
    })

    setPrSearchResultsOpen(false)
    toast.success(
      `${Math.max(addedCount, selections.length)} item(s) added`,
      { autoClose: 2000 }
    )
  }

  const PR_SEARCH_MIN_LENGTH = 3
  const PR_SEARCH_DEBOUNCE_MS = 400

  const searchProcurementsByPrNo = useCallback(
    async (searchTerm, options = {}) => {
      const { showToast = true } = options
      const prNo = String(searchTerm ?? '').trim()

      if (!prNo) {
        setPrSearchResults([])
        setPrSearchResultsOpen(false)
        if (showToast) {
          toast.error('Enter PR number to search', { autoClose: 2000 })
        }
        return
      }

      if (prNo.length < PR_SEARCH_MIN_LENGTH) {
        setPrSearchResults([])
        setPrSearchResultsOpen(false)
        return
      }

      const requestId = ++prSearchRequestIdRef.current
      setPrSearchLoading(true)

      try {
        const response = await axios.post(API_URL, {
          action: 'get-all-procurements',
          page: 1,
          limit: 5000,
          prNo
        })

        if (requestId !== prSearchRequestIdRef.current) {
          return
        }

        const responseData = parseApiResponse(response.data)
        const list = responseData?.data?.procurements || []

        if (isApiSuccess(responseData) && Array.isArray(list) && list.length > 0) {
          setPrSearchResults(list)
          setPrSearchResultsOpen(true)
          setSelectedPrKeys((prev) => {
            const next = new Set(prev)
            list.forEach((item, index) => {
              const key = getProcurementKey(item, index)
              if (isProcurementInMaterials(materials, item) || isProcurementFrozen(item)) {
                next.delete(key)
              }
              if (item.isBoq && Array.isArray(item.boq)) {
                item.boq.forEach((boqItem, boqIndex) => {
                  const boqKey = getBoqSelectionKey(key, boqItem, boqIndex)
                  if (
                    isBoqItemFrozen(boqItem) ||
                    isBoqItemInMaterials(materials, item, boqItem)
                  ) {
                    next.delete(boqKey)
                  }
                })
              }
            })
            return next
          })
          // Sync BOQ onto already-added material rows from this PR search
          setMaterials((prev) =>
            prev.map((row) => {
              const match = list.find((item) =>
                materialMatchesProcurement(row, item)
              )
              if (!match) return row
              return {
                ...row,
                lineNo: match.lineNo != null ? match.lineNo : row.lineNo,
                isBoq: !!match.isBoq,
                boq: mapBoqItems(match.boq)
              }
            })
          )
          if (showToast) {
            toast.info(`${list.length} item(s) found.`, { autoClose: 2500 })
          }
        } else {
          setPrSearchResults([])
          setPrSearchResultsOpen(false)
          if (showToast) {
            toast.info(
              responseData?.message || 'No procurement items found for this PR number',
              { autoClose: 2000 }
            )
          }
        }
      } catch (error) {
        if (requestId !== prSearchRequestIdRef.current) {
          return
        }
        console.error('PR search error:', error)
        setPrSearchResults([])
        setPrSearchResultsOpen(false)
        const errData = error.response?.data ? parseApiResponse(error.response.data) : null
        toast.error(
          errData?.message || 'Failed to search procurement by PR number',
          { autoClose: 2000 }
        )
      } finally {
        if (requestId === prSearchRequestIdRef.current) {
          setPrSearchLoading(false)
        }
      }
    },
    [materials]
  )

  const fetchMaterialCatalog = async () => {
  setIsMaterialsCatalogLoading(true)
  try {
    const response = await axios.post(API_URL, {
      action: 'get-all-material',
      page: 1,
      limit: 5000
    })
    const responseData = parseApiResponse(response.data)

    if (isApiSuccess(responseData)) {
      setMaterialCatalog(responseData.data?.materials || [])
    } else {
      setMaterialCatalog([])
    }
  } catch (err) {
    console.error('Fetch materials error:', err)
    setMaterialCatalog([])
  } finally {
    setIsMaterialsCatalogLoading(false)
  }
}

  const fetchUnits = async () => {
    setIsLoadingUnits(true)
    try {
      const response = await axios.post(API_URL, {
        action: 'get-all-units',
        page: 1,
        limit: 500,
        sortBy: 'unit',
        sortOrder: 'asc'
      })
      const responseData = parseApiResponse(response.data)
      const list = Array.isArray(responseData?.data?.units)
        ? responseData.data.units
        : []
      const activeUnits = list
        .map((item) => ({
          id: item?._id || item?.id || '',
          unit: String(item?.unit || '').trim(),
          status: String(item?.status || '').trim()
        }))
        .filter((item) => item.unit)
        .filter(
          (item) => !item.status || item.status.toLowerCase() === 'active'
        )

      setUnitOptions(isApiSuccess(responseData) ? activeUnits : [])
    } catch (err) {
      console.error('Fetch units error:', err)
      setUnitOptions([])
    } finally {
      setIsLoadingUnits(false)
    }
  }

useEffect(() => {
  fetchMaterialCatalog()
  fetchUnits()
}, [])

useEffect(() => {
  setMaterialSearchTerms((prev) => {
    const next = { ...prev }
    materials.forEach((row, index) => {
      next[index] = row.materialCode || ''
    })
    return next
  })
}, [materials])

const getFilteredMaterialCatalog = (rowIndex) => {
  const search = (materialSearchTerms[rowIndex] || '').toLowerCase()
  return materialCatalog.filter(
    (m) =>
      m.materialCode?.toLowerCase().includes(search) ||
      m.productName?.toLowerCase().includes(search) ||
      m.materialDescription?.toLowerCase().includes(search)
  )
}

const openMaterialDropdown = useCallback((rowIndex) => {
  if (isMaterialsCatalogLoading) return
  const el = materialInputRefs.current[rowIndex]
  if (el) {
    const rect = el.getBoundingClientRect()
    setMaterialDropdownPos((prev) => ({
      ...prev,
      [rowIndex]: { top: rect.bottom, left: rect.left, width: rect.width }
    }))
  }
  setShowMaterialDropdown((prev) => ({ ...prev, [rowIndex]: true }))
}, [isMaterialsCatalogLoading])

const closeMaterialDropdown = (rowIndex) => {
  setShowMaterialDropdown((prev) => ({ ...prev, [rowIndex]: false }))
  setSelectedMaterialIndex((prev) => ({ ...prev, [rowIndex]: -1 }))
}
const handleMaterialSearchChange = (rowIndex, value) => {
  setMaterialSearchTerms((prev) => ({ ...prev, [rowIndex]: value }))
  setSelectedMaterialIndex((prev) => ({ ...prev, [rowIndex]: -1 }))
  openMaterialDropdown(rowIndex)
  updateMaterial(rowIndex, 'materialCode', value)
}

const handleMaterialSelect = (rowIndex, material) => {

  setMaterials((prev) =>
    prev.map((row, i) =>
      i === rowIndex
        ? {
            ...row,
            materialCode: material.materialCode || '',
            materialDescription: material.productName || material.materialDescription || '',
            hsn: material.hsnCode || material.hsn || '',
            uom: material.uom || ''
          }
        : row
    )
  )

  setMaterialSearchTerms((prev) => ({ ...prev, [rowIndex]: material.materialCode }))
  closeMaterialDropdown(rowIndex)
}

const handleMaterialKeyDown = (rowIndex, e) => {
  if (!showMaterialDropdown[rowIndex]) return
  const list = getFilteredMaterialCatalog(rowIndex)
  const currentIndex = selectedMaterialIndex[rowIndex] ?? -1

  if (e.key === 'ArrowDown') {
    e.preventDefault()
    setSelectedMaterialIndex((prev) => ({
      ...prev,
      [rowIndex]: currentIndex < list.length - 1 ? currentIndex + 1 : currentIndex
    }))
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    setSelectedMaterialIndex((prev) => ({
      ...prev,
      [rowIndex]: currentIndex > 0 ? currentIndex - 1 : 0
    }))
  } else if (e.key === 'Enter') {
    if (currentIndex >= 0 && list[currentIndex]) {
      e.preventDefault()
      handleMaterialSelect(rowIndex, list[currentIndex])
    }
  }
}

useEffect(() => {
  const activeRowIndex = Object.keys(showMaterialDropdown).find(
    (idx) => showMaterialDropdown[idx]
  )
  if (activeRowIndex === undefined) return

  const reposition = () => openMaterialDropdown(activeRowIndex)
  window.addEventListener('scroll', reposition, true)
  window.addEventListener('resize', reposition)
  return () => {
    window.removeEventListener('scroll', reposition, true)
    window.removeEventListener('resize', reposition)
  }
}, [showMaterialDropdown, openMaterialDropdown])

  const handlePrSearchChange = (e) => {
    const value = e.target.value
    setPrNoSearch(value)
    prSearchRequestIdRef.current += 1

    if (prSearchDebounceRef.current) {
      clearTimeout(prSearchDebounceRef.current)
    }

    const trimmed = value.trim()
    if (trimmed.length < PR_SEARCH_MIN_LENGTH) {
      setPrSearchLoading(false)
      setPrSearchResults([])
      setPrSearchResultsOpen(false)
      return
    }

    prSearchDebounceRef.current = setTimeout(() => {
      searchProcurementsByPrNo(trimmed, { showToast: false })
    }, PR_SEARCH_DEBOUNCE_MS)
  }

  const handlePrNoSearch = () => {
    if (prSearchDebounceRef.current) {
      clearTimeout(prSearchDebounceRef.current)
    }
    searchProcurementsByPrNo(prNoSearch, { showToast: true })
  }

  const handlePrSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handlePrNoSearch()
    }
  }

  useEffect(() => {
    return () => {
      if (prSearchDebounceRef.current) {
        clearTimeout(prSearchDebounceRef.current)
      }
      if (supplierSearchDebounceRef.current) {
        clearTimeout(supplierSearchDebounceRef.current)
      }
      if (ccSearchDebounceRef.current) {
        clearTimeout(ccSearchDebounceRef.current)
      }
    }
  }, [])

  const getFilteredMaterialDescCatalog = (rowIndex) => {
  const search = (materials[rowIndex]?.materialDescription || '').toLowerCase()
  return materialCatalog.filter((m) => {
    return (
      (m.productName || '').toLowerCase().includes(search) ||
      (m.categoryName || '').toLowerCase().includes(search) ||
      (m.subcategoryName || '').toLowerCase().includes(search) ||
      (m.partNumber || '').toLowerCase().includes(search) ||
      (m.makeBrandOem || '').toLowerCase().includes(search)
    )
  })
}

const openMaterialDescDropdown = useCallback((rowIndex) => {
  if (isMaterialsCatalogLoading) return
  const el = materialDescInputRefs.current[rowIndex]
  if (el) {
    const rect = el.getBoundingClientRect()
    setMaterialDescDropdownPos((prev) => ({
      ...prev,
      [rowIndex]: { top: rect.bottom, left: rect.left, width: rect.width }
    }))
  }
  setShowMaterialDescDropdown((prev) => ({ ...prev, [rowIndex]: true }))
}, [isMaterialsCatalogLoading])

const closeMaterialDescDropdown = (rowIndex) => {
  setShowMaterialDescDropdown((prev) => ({ ...prev, [rowIndex]: false }))
  setSelectedMaterialDescIndex((prev) => ({ ...prev, [rowIndex]: -1 }))
}

const handleMaterialDescSearchChange = (rowIndex, value) => {
  updateMaterial(rowIndex, 'materialDescription', value)
  setSelectedMaterialDescIndex((prev) => ({ ...prev, [rowIndex]: -1 }))
  openMaterialDescDropdown(rowIndex)
}

const handleMaterialDescSelect = (rowIndex, material) => {

  setMaterials((prev) =>
    prev.map((row, i) =>
      i === rowIndex
        ? {
            ...row,
            materialCode: material.materialCode || row.materialCode,
            materialDescription:
              material.productName || material.materialDescription || row.materialDescription,
            hsn: material.hsnCode || material.hsn || row.hsn,
            uom: material.uom || row.uom
          }
        : row
    )
  )

  setMaterialSearchTerms((prev) => ({ ...prev, [rowIndex]: material.materialCode || '' }))

  closeMaterialDescDropdown(rowIndex)
}

const handleMaterialDescKeyDown = (rowIndex, e) => {
  if (!showMaterialDescDropdown[rowIndex]) return
  const list = getFilteredMaterialDescCatalog(rowIndex)
  const currentIndex = selectedMaterialDescIndex[rowIndex] ?? -1

  if (e.key === 'ArrowDown') {
    e.preventDefault()
    setSelectedMaterialDescIndex((prev) => ({
      ...prev,
      [rowIndex]: currentIndex < list.length - 1 ? currentIndex + 1 : currentIndex
    }))
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    setSelectedMaterialDescIndex((prev) => ({
      ...prev,
      [rowIndex]: currentIndex > 0 ? currentIndex - 1 : 0
    }))
  } else if (e.key === 'Enter') {
    if (currentIndex >= 0 && list[currentIndex]) {
      e.preventDefault()
      handleMaterialDescSelect(rowIndex, list[currentIndex])
    }
  } else if (e.key === 'Escape') {
    closeMaterialDescDropdown(rowIndex)
  }
}

useEffect(() => {
  const activeRowIndex = Object.keys(showMaterialDescDropdown).find(
    (idx) => showMaterialDescDropdown[idx]
  )
  if (activeRowIndex === undefined) return

  const reposition = () => openMaterialDescDropdown(activeRowIndex)
  window.addEventListener('scroll', reposition, true)
  window.addEventListener('resize', reposition)
  return () => {
    window.removeEventListener('scroll', reposition, true)
    window.removeEventListener('resize', reposition)
  }
}, [showMaterialDescDropdown, openMaterialDescDropdown])

  const validateDetails = () => {
    if (isFetchingRfqNumber) {
      toast.error('RFQ number is still loading. Please wait.', { autoClose: 2000 })
      return false
    }
    if (!rfqDetails.rfqNumber.trim()) {
      toast.error('RFQ number is required', { autoClose: 2000 })
      return false
    }
    if (!rfqDetails.rfqDate) {
      toast.error('RFQ date is required', { autoClose: 2000 })
      return false
    }
    if (!rfqDetails.rfqDueDate) {
      toast.error('RFQ due date is required', { autoClose: 2000 })
      return false
    }
    if (!rfqDetails.rfqDueTime) {
      toast.error('RFQ due time is required', { autoClose: 2000 })
      return false
    }
    if (isDueBeforeRfqDate(rfqDetails.rfqDate, rfqDetails.rfqDueDate, rfqDetails.rfqDueTime)) {
      toast.error('Due date and time cannot be before RFQ date', { autoClose: 2000 })
      return false
    }
    if (!rfqDetails.rfqSummary.trim()) {
      toast.error('RFQ summary is required', { autoClose: 2000 })
      return false
    }
    if (!rfqDetails.rfqCurrency.trim()) {
      toast.error('Currency is required', { autoClose: 2000 })
      return false
    }
    return true
  }

  const validateMaterials = () => {
    const filled = materials.filter(
      (m) =>
        m.materialCode.trim() ||
        m.materialDescription.trim() ||
        m.quantity !== ''
    )
    if (filled.length === 0) {
      toast.error('Add at least one material line', { autoClose: 2000 })
      return false
    }
    for (let i = 0; i < filled.length; i++) {
      const row = filled[i]
      const idx = materials.indexOf(row) + 1
      if (!row.materialCode.trim()) {
        toast.error(`Row ${idx}: Material code is required`, { autoClose: 2000 })
        return false
      }
      if (!row.materialDescription.trim()) {
        toast.error(`Row ${idx}: Material description is required`, { autoClose: 2000 })
        return false
      }
      if (row.quantity === '' || isNaN(parseFloat(row.quantity))) {
        toast.error(`Row ${idx}: Valid quantity is required`, { autoClose: 2000 })
        return false
      }
    }

    return true
  }

  const hasCompleteDetails = () => {
    if (isFetchingRfqNumber || !rfqDetails.rfqNumber.trim()) return false
    if (!rfqDetails.rfqDate || !rfqDetails.rfqDueDate || !rfqDetails.rfqDueTime) return false
    if (isDueBeforeRfqDate(rfqDetails.rfqDate, rfqDetails.rfqDueDate, rfqDetails.rfqDueTime)) {
      return false
    }
    if (!rfqDetails.rfqSummary.trim() || !rfqDetails.rfqCurrency.trim()) return false
    return true
  }

  const hasCompleteMaterials = () => {
    const filled = materials.filter(
      (m) =>
        m.materialCode.trim() ||
        m.materialDescription.trim() ||
        m.quantity !== ''
    )
    if (filled.length === 0) return false
    return filled.every(
      (row) =>
        row.materialCode.trim() &&
        row.materialDescription.trim() &&
        row.quantity !== '' &&
        !isNaN(parseFloat(row.quantity))
    )
  }

  const hasCompleteSuppliers = () => {
    const filled = suppliers.filter(
      (s) => s.supplierName.trim() && s.supplierMailId.trim()
    )
    if (filled.length === 0) return false
    return filled.every((row) =>
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.supplierMailId.trim())
    )
  }

  const isReadyToSendEmail =
    canAccessPreview &&
    Boolean(rfqDetails.pdfLink?.trim()) &&
    hasCompleteDetails() &&
    hasCompleteMaterials() &&
    hasCompleteSuppliers()

  const getPendingSuppliers = () =>
    suppliers.filter(
      (s) =>
        s.supplierName.trim() &&
        s.supplierMailId.trim() &&
        !supplierAlreadyEmailed(s)
    )

  const getAlreadyEmailedSuppliers = () =>
    suppliers.filter(
      (s) =>
        s.supplierName.trim() &&
        s.supplierMailId.trim() &&
        supplierAlreadyEmailed(s)
    )

  const hasPendingSuppliersToEmail = () => getPendingSuppliers().length > 0

  const getFilledMaterials = () =>
    materials.filter(
      (m) =>
        m.materialCode.trim() ||
        m.materialDescription.trim() ||
        m.quantity !== ''
    )

  const getFilledSuppliers = () => buildSuppliersPayload()

  const validateSupplierTab = () => {
    const filled = suppliers.filter(
      (s) => s.supplierName.trim() && s.supplierMailId.trim()
    )
    if (filled.length === 0) {
      toast.error('Add at least one supplier with name and email', { autoClose: 2000 })
      return false
    }
    for (let i = 0; i < filled.length; i++) {
      const row = filled[i]
      const idx = suppliers.indexOf(row) + 1
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.supplierMailId.trim())) {
        toast.error(`Supplier row ${idx}: Enter a valid email`, { autoClose: 2000 })
        return false
      }
    }
    return true
  }

  const buildTermsPayload = () =>
    terms
      .filter((t) => t.rfqTerm.trim())
      .map((t) => ({
        rfqTerm: t.rfqTerm.trim(),
        termsResponse: (t.termsResponse || '').trim()
      }))

  const buildMaterialsPayload = () =>
    materials
      .filter(
        (m) =>
          m.materialCode.trim() ||
          m.materialDescription.trim() ||
          m.quantity !== ''
      )
      .map((m) => {
        const row = {
          materialCode: m.materialCode.trim(),
          materialDescription: m.materialDescription.trim(),
          hsn: m.hsn.trim(),
          uom: m.uom.trim(),
          quantity: parseFloat(m.quantity) || 0
        }
        const prNumber = String(m.prNumber || '').trim()
        if (prNumber) {
          row.prNumber = prNumber
        }
        const prId = normalizePrId(m.pr_id || m.prId)
        if (prId) {
          row.pr_id = prId
        }
        const clientReference = String(m.clientReference || '').trim()
        if (clientReference) {
          row.clientReference = clientReference
        }
        if (m.lineNo != null && m.lineNo !== '') {
          row.lineNo = m.lineNo
        }
        const filledBoq = (Array.isArray(m.boq) ? m.boq : []).filter(
          (item) =>
            String(item.materialCode || '').trim() ||
            String(item.materialDescription || '').trim() ||
            String(item.quantity || '').trim()
        )
        const isBoq = !!m.isBoq && filledBoq.length > 0
        row.isBoq = isBoq
        if (isBoq) {
          row.boq = filledBoq.map((item, boqIndex) => {
            const boqRow = {
              boqNo: item.boqNo || `${m.lineNo || ''}.${boqIndex + 1}`,
              materialCode: String(item.materialCode || '').trim(),
              materialDescription: String(
                item.materialDescription || ''
              ).trim(),
              hsnCode: String(item.hsnCode || item.hsn || '').trim(),
              uom: String(item.uom || '').trim(),
              quantity: parseFloat(item.quantity) || 0
            }
            const boqId = getBoqId(item)
            if (boqId) {
              boqRow.boq_id = boqId
            }
            if (item.prNo) {
              boqRow.prNo = item.prNo
            }
            return boqRow
          })
        }
        return row
      })

   const buildSuppliersPayload = () =>
  suppliers
    .filter((s) => s.supplierName.trim() || s.supplierMailId.trim())
    .map((s) => {
      const row = {
        supplierName: s.supplierName.trim(),
        supplierMailId: s.supplierMailId.trim()
      }
      const phoneNumber = String(s.phoneNumber || '').trim()
      if (phoneNumber) {
        row.phoneNumber = phoneNumber
      }
      const supplierId = String(s.supplierId || '').trim()
      if (supplierId) {
        row.supplierId = supplierId
      }

      const contactPerson = String(s.contactPerson || '').trim()
      if (contactPerson) row.contactPerson = contactPerson

      const address = String(s.address || '').trim()
      if (address) row.address = address

      const gstNumber = String(s.gstNumber || '').trim()
      if (gstNumber) row.gstNumber = gstNumber

      const gstStatus = String(s.gstStatus || '').trim()
      if (gstStatus) row.gstStatus = gstStatus

      const gstLegalName = String(s.gstLegalName || '').trim()
      if (gstLegalName) row.gstLegalName = gstLegalName

      const gstTradeName = String(s.gstTradeName || '').trim()
      if (gstTradeName) row.gstTradeName = gstTradeName

      const gstConstitutionType = String(s.gstConstitutionType || '').trim()
      if (gstConstitutionType) row.gstConstitutionType = gstConstitutionType

      const gstTaxPayerType = String(s.gstTaxPayerType || '').trim()
      if (gstTaxPayerType) row.gstTaxPayerType = gstTaxPayerType

      const gstRegistrationDate = String(s.gstRegistrationDate || '').trim()
      if (gstRegistrationDate) row.gstRegistrationDate = gstRegistrationDate

      const gstState = String(s.gstState || '').trim()
      if (gstState) row.gstState = gstState

      const city = String(s.city || '').trim()
      if (city) row.city = city

      const state = String(s.state || '').trim()
      if (state) row.state = state

      return row
    })

  const getUserId = () => {
    const userId = localStorage.getItem('id')
    if (!userId) {
      toast.error('User session expired. Please login again.', { autoClose: 2000 })
      setTimeout(() => navigate('/'), 2000)
      return null
    }
    return userId
  }

  const buildTab1DetailsFields = () => {
    const fields = {
      rfqNumber: rfqDetails.rfqNumber.trim(),
      rfqDate: formatDateForApi(rfqDetails.rfqDate),
      rfqDueDate: formatDateTimeForApi(rfqDetails.rfqDueDate, rfqDetails.rfqDueTime),
      rfqSummary: rfqDetails.rfqSummary.trim(),
      rfqCurrency: rfqDetails.rfqCurrency.trim(),
      rfqStatus: DEFAULT_RFQ_STATUS,
      terms: buildTermsPayload()
    }
    if (rfqDetails.pdfLink.trim()) {
      fields.pdfLink = rfqDetails.pdfLink.trim()
    }
    return fields
  }

  const postRfqRequest = async (payload) => {
    const response = await axios.post(API_URL, payload)
    const responseData = parseApiResponse(response.data)

    if (!isApiSuccess(responseData)) {
      toast.error(responseData?.message || 'Failed to save RFQ', { autoClose: 2000 })
      return { ok: false, responseData }
    }

    return { ok: true, responseData }
  }

const saveTab1Details = async ({ silent = false } = {}) => {
  const userId = getUserId()
  if (!userId) return false
  if (!silent) setIsLoading(true)
  try {
    let payload
    if (rfqId) {
      payload = { action: 'update-rfq', id: rfqId, ...buildTab1DetailsFields(), updatedBy: userId }
    } else {
      payload = { action: 'create-rfq', ...buildTab1DetailsFields(), materials: [], suppliers: [], createdBy: userId }
    }
    const { ok, responseData } = await postRfqRequest(payload)
    if (!ok) return false
    const newId = rfqId || extractCreatedRfqId(responseData)
    if (newId && !rfqId) {
      setRfqId(newId)
      navigate(`/rfqCreate/${newId}`, { replace: true })
    }
    if (!silent) toast.success('Draft saved', { autoClose: 1500 })
    return true
  } catch (error) {
    console.error('Save RFQ details error:', error)
    const errData = error.response?.data ? parseApiResponse(error.response.data) : null
    if (!silent) toast.error(errData?.message || 'An error occurred while saving RFQ', { autoClose: 2000 })
    return false
  } finally {
    if (!silent) setIsLoading(false)
  }
}

const saveTab2Materials = async ({ silent = false } = {}) => {
  const userId = getUserId()
  if (!userId) return false
  if (!rfqId) return false
  if (!silent) setIsLoading(true)
  try {
    const { ok } = await postRfqRequest({
      action: 'update-rfq', id: rfqId, materials: buildMaterialsPayload(), updatedBy: userId
    })
    if (!ok) return false
    if (!silent) toast.success('Materials saved', { autoClose: 1500 })
    return true
  } catch (error) {
    console.error('Save RFQ materials error:', error)
    const errData = error.response?.data ? parseApiResponse(error.response.data) : null
    if (!silent) toast.error(errData?.message || 'An error occurred while saving materials', { autoClose: 2000 })
    return false
  } finally {
    if (!silent) setIsLoading(false)
  }
}

const saveTabAttachments = async ({ silent = false } = {}) => {
  const userId = getUserId()
  if (!userId) return false
  if (!rfqId) return false
  if (!silent) setIsLoading(true)
  try {
    if (pendingAttachmentFiles.length > 0) {
      const uploaded = await persistPendingAttachments()
      if (!uploaded) return false
      if (!silent) toast.success('Attachments saved', { autoClose: 1500 })
    }
    return true
  } finally {
    if (!silent) setIsLoading(false)
  }
}


const TAB_ORDER = ['details', 'materials', 'attachments', 'supplier', 'preview']
const isForwardMove = (fromTab, toTab) => TAB_ORDER.indexOf(toTab) > TAB_ORDER.indexOf(fromTab)

const goToTab = async (nextTab) => {
  if (nextTab === activeTab) return
  if (nextTab === 'preview' && !canAccessPreview) return

  const forward = isForwardMove(activeTab, nextTab)

  if (activeTab === 'details') {
    if (forward) {
      if (!validateDetails()) return
      if (!(await saveTab1Details())) return
    } else {
      await saveTab1Details({ silent: true })
    }
  } else if (activeTab === 'materials') {
    if (forward) {
      if (!validateMaterials()) return
      if (!(await saveTab2Materials())) return
    } else {
      await saveTab2Materials({ silent: true })
    }
  } else if (activeTab === 'attachments') {
    await saveTabAttachments({ silent: true })
  } else if (activeTab === 'supplier') {
    if (!forward) {
      await saveTab3Suppliers()
    }
  }

  setActiveTab(nextTab)
}

const handleBack = async (e) => {
  e.preventDefault()
  if (activeTab === 'details') {
    await saveTab1Details({ silent: true })
  } else if (activeTab === 'materials' && rfqId) {
    await saveTab2Materials({ silent: true })
  } else if (activeTab === 'attachments' && rfqId) {
    await saveTabAttachments({ silent: true })
  } else if (activeTab === 'supplier' && rfqId) {
    await saveTab3Suppliers()
  }
  navigate('/rfq')
}
  const persistPendingAttachments = async () => {
    if (pendingAttachmentFiles.length === 0) return true

    const userId = getUserId()
    if (!userId) return false

    const rfqNumber = rfqDetails.rfqNumber.trim()
    if (!rfqNumber) {
      toast.error('RFQ number is required for attachments', { autoClose: 2000 })
      return false
    }

    setIsUploadingAttachments(true)
    try {
      const files = pendingAttachmentFiles
      const contentTypesForPresign = files.map((f) => resolveFileContentType(f))

      const response = await axios.post(API_URL, {
        action: 'get-rfq-owner-attachment-upload-presigned-url',
        rfqNumber,
        fileNames: files.map((f) => f.name),
        contentTypes: contentTypesForPresign,
        expiresIn: 7200,
      })
      const responseData = parseApiResponse(response.data)

      if (!isApiSuccess(responseData) || !Array.isArray(responseData.files)) {
        toast.error(responseData?.message || 'Failed to get upload URLs', { autoClose: 2000 })
        return false
      }

      const presignedFiles = responseData.files
      if (presignedFiles.length !== files.length) {
        toast.error('Upload URL count mismatch', { autoClose: 2000 })
        return false
      }

      // S3 PUT via raw fetch — never shared API axios (currentUserId ~44B bug).
      for (let i = 0; i < files.length; i++) {
        const meta = presignedFiles[i]
        const file = files[i]
        const contentType =
          meta.contentType ||
          contentTypesForPresign[i] ||
          'application/octet-stream'

        await putFileToPresignedUrl(meta.uploadUrl, file, contentType)
      }

      const s3Keys = presignedFiles.map((f) => f.s3Key)
      const originalFileNames = presignedFiles.map(
        (f, idx) => f.originalFileName || f.fileName || files[idx].name
      )
      const savedContentTypes = presignedFiles.map(
        (f, idx) =>
          f.contentType || contentTypesForPresign[idx] || 'application/octet-stream'
      )

      const updatePayload = {
        action: 'update-rfq',
        id: rfqId,
        updatedBy: userId,
        s3Keys,
        originalFileNames,
        contentTypes: savedContentTypes
      }
      if (rfqOwnerAttachments.length > 0) {
        updatePayload.appendRfqOwner = true
      }

      const { ok, responseData: updateData } = await postRfqRequest(updatePayload)
      if (!ok) return false

      const updatedRfq = updateData?.data
      if (Array.isArray(updatedRfq?.rfqOwner)) {
        setRfqOwnerAttachments(updatedRfq.rfqOwner.map(mapRfqOwnerFromApi))
      } else {
        const newEntries = presignedFiles.map((f, idx) => ({
          s3Key: f.s3Key,
          originalFileName: originalFileNames[idx],
          contentType: savedContentTypes[idx],
          uploadedAt: new Date().toISOString(),
          url: ''
        }))
        setRfqOwnerAttachments((prev) => [...prev, ...newEntries])
      }

      setPendingAttachmentFiles([])
      return true
    } catch (error) {
      console.error('Upload attachments error:', error)
      const errData = error.response?.data ? parseApiResponse(error.response.data) : null
      toast.error(errData?.message || 'Failed to upload attachments', { autoClose: 2000 })
      return false
    } finally {
      setIsUploadingAttachments(false)
    }
  }

  const handleAttachmentFileSelect = (e) => {
    const files = Array.from(e.target.files || [])
    e.target.value = ''
    if (files.length === 0) return

    const totalCount =
      rfqOwnerAttachments.length + pendingAttachmentFiles.length + files.length
    if (totalCount > MAX_RFQ_OWNER_ATTACHMENTS) {
      toast.error(`Maximum ${MAX_RFQ_OWNER_ATTACHMENTS} attachments allowed`, {
        autoClose: 2000
      })
      return
    }

    setPendingAttachmentFiles((prev) => [...prev, ...files])
  }

  const removePendingAttachment = (index) => {
    setPendingAttachmentFiles((prev) => prev.filter((_, i) => i !== index))
  }

  const downloadRfqOwnerAttachment = async (s3Key) => {
    if (!s3Key) return
    try {
      const response = await axios.post(API_URL, {
        action: 'get-rfq-owner-attachment-download-presigned-url',
        s3Keys: [s3Key],
        expiresIn: 3600
      })
      const responseData = parseApiResponse(response.data)
      if (!isApiSuccess(responseData)) {
        toast.error(responseData?.message || 'Failed to get download URL', { autoClose: 2000 })
        return
      }
      const downloadUrl =
        responseData.files?.[0]?.downloadUrl || responseData.downloadUrl
      if (!downloadUrl) {
        toast.error('Download URL not available', { autoClose: 2000 })
        return
      }
      window.open(downloadUrl, '_blank', 'noopener,noreferrer')
    } catch (error) {
      console.error('Download attachment error:', error)
      toast.error('Failed to download attachment', { autoClose: 2000 })
    }
  }

  const removeRfqOwnerAttachment = async (s3Key) => {
    const userId = getUserId()
    if (!userId || !rfqId) return

    setIsUploadingAttachments(true)
    try {
      const remaining = rfqOwnerAttachments.filter((a) => a.s3Key !== s3Key)
      const { ok, responseData } = await postRfqRequest({
        action: 'update-rfq',
        id: rfqId,
        updatedBy: userId,
        rfqOwner: remaining.map((a) => ({
          s3Key: a.s3Key,
          originalFileName: a.originalFileName,
          contentType: a.contentType
        }))
      })
      if (!ok) return
      if (Array.isArray(responseData?.data?.rfqOwner)) {
        setRfqOwnerAttachments(responseData.data.rfqOwner.map(mapRfqOwnerFromApi))
      } else {
        setRfqOwnerAttachments(remaining)
      }
      toast.success('Attachment removed', { autoClose: 1500 })
    } finally {
      setIsUploadingAttachments(false)
    }
  }

  const saveTab3Suppliers = async () => {
    const userId = getUserId()
    if (!userId) return false
    if (!rfqId) {
      toast.error('Save RFQ details first', { autoClose: 2000 })
      return false
    }

    const { ok } = await postRfqRequest({
      action: 'update-rfq',
      id: rfqId,
      suppliers: buildSuppliersPayload(),
      updatedBy: userId
    })
    return ok
  }

  const activateRfq = async () => {
    const userId = getUserId()
    if (!userId) return false
    if (!rfqId) {
      toast.error('Save RFQ details first', { autoClose: 2000 })
      return false
    }

    setIsLoading(true)
    try {
      if (pendingAttachmentFiles.length > 0) {
        const uploaded = await persistPendingAttachments()
        if (!uploaded) return false
      }

      const suppliersSaved = await saveTab3Suppliers()
      if (!suppliersSaved) return false

      const { ok, responseData } = await postRfqRequest({
        action: 'update-rfq',
        id: rfqId,
        status: FINAL_STATUS,
        materials: buildMaterialsPayload(),
        suppliers: buildSuppliersPayload(),
        updatedBy: userId
      })
      if (!ok) return false

      setRfqRecordStatus(FINAL_STATUS)
      setShowPreviewStep(true)

      const pdfFromResponse = extractPdfLink(responseData)
      if (pdfFromResponse) {
        setRfqDetails((prev) => ({ ...prev, pdfLink: pdfFromResponse }))
      } else {
        await refreshRfqById({ preserveActiveStatus: true })
      }

      setActiveTab('preview')
      toast.success('RFQ created. Review PDF and send email.', { autoClose: 2000 })
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return true
    } catch (error) {
      console.error('Activate RFQ error:', error)
      const errData = error.response?.data ? parseApiResponse(error.response.data) : null
      toast.error(errData?.message || 'An error occurred while creating RFQ', { autoClose: 2000 })
      return false
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (
      !validateDetails() ||
      !validateMaterials() ||
      !validateSupplierTab()
    ) {
      return
    }

    await activateRfq()
  }

  const sendRfqEmail = async () => {
    if (!rfqId) {
      toast.error('Save RFQ details first', { autoClose: 2000 })
      return
    }
    if (!isRfqActive) {
      toast.error('Activate RFQ before sending email', { autoClose: 2000 })
      return
    }
    if (
      !validateDetails() ||
      !validateMaterials() ||
      !validateSupplierTab()
    ) {
      return
    }
    if (!rfqDetails.pdfLink?.trim()) {
      toast.error('PDF preview is not available yet. Please try again shortly.', {
        autoClose: 2000
      })
      return
    }

    if (!hasPendingSuppliersToEmail()) {
      toast.info('All suppliers have already been emailed.', { autoClose: 2500 })
      return
    }
    const userId = getUserId()
    if (!userId) return

    const ccEmails = buildCcEmails(userEmail, additionalCcEmails)
    const invalidCc = ccEmails.find((email) => !isValidEmailAddress(email))
    if (invalidCc) {
      toast.error(`Invalid CC email: ${invalidCc}`, { autoClose: 2000 })
      return
    }
    if (ccEmails.length === 0) {
      toast.error('Could not get your email for CC. Add CC emails or check your profile.', {
        autoClose: 2000
      })
      return
    }

    setIsSendingEmail(true)
    try {
      const response = await axios.post(API_URL, {
        action: 'send-rfq-email',
        id: rfqId,
        userid: userId,
        ccEmails
      })
      const responseData = parseApiResponse(response.data)

      if (!isApiSuccess(responseData)) {
        toast.error(responseData?.message || 'Failed to send RFQ email', { autoClose: 2000 })
        return
      }

      const data = responseData?.data || {}
      const results = Array.isArray(data.results) ? data.results : []
      const skipped = Array.isArray(data.skippedAlreadyEmailed)
        ? data.skippedAlreadyEmailed
        : []
      const totalRecipients = Number(data.totalRecipients)
      const sentCount = Number.isFinite(totalRecipients) ? totalRecipients : results.length

      if (sentCount === 0) {
        const skippedMsg =
          skipped.length > 0
            ? `Already emailed: ${skipped.join(', ')}`
            : responseData?.message || 'No new supplier emails to send'
        toast.info(skippedMsg, { autoClose: 3000 })
        await refreshRfqById()
        return
      }

      const resultByMail = new Map(
        results.map((row) => [String(row.to || '').trim().toLowerCase(), row])
      )
      setSuppliers((prev) =>
        prev.map((supplier) => {
          const mail = supplier.supplierMailId.trim().toLowerCase()
          const result = resultByMail.get(mail)
          if (result?.success !== false && result?.quoteLink) {
            return { ...supplier, emailLink: result.quoteLink }
          }
          return supplier
        })
      )

      setIsMailSent(true)
      let successMessage = responseData?.message || 'RFQ email sent successfully'
      if (skipped.length > 0) {
        successMessage += ` Skipped (already emailed): ${skipped.join(', ')}`
      }
      toast.success(successMessage, { autoClose: 3000 })
      await refreshRfqById()
    } catch (error) {
      console.error('Send RFQ email error:', error)
      const errData = error.response?.data ? parseApiResponse(error.response.data) : null
      toast.error(errData?.message || 'An error occurred while sending email', {
        autoClose: 2000
      })
    } finally {
      setIsSendingEmail(false)
    }
  }


  const tabClass = (tab) => (activeTab === tab ? 'nav-link active' : 'nav-link')

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2 align-items-center">
            <div className="col-6 col-sm-6">
              <h1 className="m-0 txt_lft">Create RFQ</h1>
            </div>
            <div className="col-6 col-sm-6">
              <ol className="breadcrumb justify-content-end">
                <li className="breadcrumb-item">
                   <Link to="/rfq" onClick={handleBack}>
                     <i className="fa-solid fa-arrow-left-long" /> Back
                   </Link>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="card card-primary card-outline card-tabs">
                <div className="card-header p-0 pt-1 border-bottom-0">
                  <ul className="nav nav-tabs" role="tablist">
                    <li className="nav-item">
                      <button
                        type="button"
                        className={tabClass('details')}
                        onClick={() => goToTab('details')}
                      >
                        RFQ Details
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        type="button"
                        className={tabClass('materials')}
                        onClick={() => goToTab('materials')}
                      >
                        Materials
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        type="button"
                        className={tabClass('attachments')}
                        onClick={() => goToTab('attachments')}
                      >
                        Attachments
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        type="button"
                        className={tabClass('supplier')}
                        onClick={() => goToTab('supplier')}
                      >
                        Supplier Email
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        type="button"
                        className={tabClass('preview')}
                        onClick={() => canAccessPreview && goToTab('preview')}
                        disabled={!canAccessPreview}
                        style={!canAccessPreview ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
                      >
                        Preview & Email
                      </button>
                    </li>
                  </ul>
                </div>

                <form onSubmit={handleSubmit}>
                  {isLoadingRfq ? (
                    <div className="card-body text-center py-5">
                      <PulseLoader color="#1199B2" size={10} />
                    </div>
                  ) : (
                  <div className="card-body">
                    {activeTab === 'details' && (
                      <div className="row">
                        <div className="col-md-3">
                          <div className="form-group">
                            <label>RFQ Number</label>
                            <div style={{ position: 'relative' }}>
                              <input
                                type="text"
                                name="rfqNumber"
                                className="form-control"
                                placeholder={
                                  isFetchingRfqNumber ? 'Loading...' : 'RFQ-ITS-0001'
                                }
                                value={rfqDetails.rfqNumber}
                                readOnly
                                disabled={isFetchingRfqNumber}
                                style={{ backgroundColor: '#e9ecef', cursor: 'not-allowed' }}
                                required
                              />
                              {isFetchingRfqNumber && (
                                <div
                                  style={{
                                    position: 'absolute',
                                    right: '10px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    pointerEvents: 'none'
                                  }}
                                >
                                  <PulseLoader color="#1199B2" size={6} />
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="col-md-2">
                          <div className="form-group">
                            <label>RFQ Date</label>
                            <input
                              type="date"
                              name="rfqDate"
                              className="form-control"
                              value={rfqDetails.rfqDate}
                              onChange={handleDetailsChange}
                              required
                            />
                          </div>
                        </div>
                        <div className="col-md-2">
                          <div className="form-group">
                            <label>RFQ Due Date</label>
                            <input
                              type="date"
                              name="rfqDueDate"
                              className="form-control"
                              value={rfqDetails.rfqDueDate}
                              onChange={handleDetailsChange}
                              min={rfqDetails.rfqDate || undefined}
                              required
                            />
                          </div>
                        </div>
                        <div className="col-md-2">
                          <div className="form-group">
                            <label>Due Time</label>
                            <DueTimePicker
                              value={rfqDetails.rfqDueTime}
                              onChange={handleDueTimeChange}
                              required={Boolean(rfqDetails.rfqDueDate)}
                              placeholder="Select time"
                            />
                          </div>
                        </div>
                        <div className="col-md-2">
                          <div className="form-group">
                            <label>Currency</label>
                            <select
                              name="rfqCurrency"
                              className="form-control"
                              value={rfqDetails.rfqCurrency}
                              onChange={handleDetailsChange}
                            >
                              {CURRENCY_OPTIONS.map((c) => (
                                <option key={c} value={c}>
                                  {c}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <div className="col-md-12">
                          <div className="form-group">
                            <label>RFQ Summary</label>
                            <textarea
                              name="rfqSummary"
                              className="form-control"
                              rows={4}
                              placeholder="Summary"
                              value={rfqDetails.rfqSummary}
                              onChange={handleDetailsChange}
                              required
                            />
                          </div>
                        </div>

                        

                        <div className="col-md-12 mt-3">
                          <label className="mb-2">Terms</label>
                          <div style={{ overflowX: 'auto' }}>
                            <table
                              className="table table-bordered table-hover mb-0"
                              style={{ tableLayout: 'fixed', width: '100%' }}
                            >
                              <colgroup>
                                <col style={{ width: '56px' }} />
                                <col style={{ width: '28%' }} />
                                <col style={{ width: 'calc(72% - 146px)' }} />
                                <col style={{ width: '90px' }} />
                              </colgroup>
                              <thead>
                                <tr style={{ backgroundColor: '#003366', color: '#fff' }}>
                                  <th style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>S.No</th>
                                  <th style={{ fontSize: '12px' }}>RFQ Term</th>
                                  <th style={{ fontSize: '12px' }}>Terms Response</th>
                                  <th style={{ fontSize: '12px' }}>Action</th>
                                </tr>
                              </thead>
                              <tbody>
                                {terms.map((row, index) => (
                                  <tr key={index}>
                                    <td style={{ verticalAlign: 'top' }}>{index + 1}</td>
                                    <td style={{ verticalAlign: 'top' }}>
                                      <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        value={row.rfqTerm}
                                        onChange={(e) => updateTerm(index, 'rfqTerm', e.target.value)}
                                        style={{ width: '100%' }}
                                      />
                                    </td>
                                    <td style={{ verticalAlign: 'top' }}>
                                      <textarea
                                        className="form-control form-control-sm"
                                        rows={2}
                                        value={row.termsResponse}
                                        onChange={(e) =>
                                          updateTerm(index, 'termsResponse', e.target.value)
                                        }
                                        style={{
                                          width: '100%',
                                          resize: 'vertical',
                                          whiteSpace: 'pre-wrap',
                                          wordBreak: 'break-word'
                                        }}
                                      />
                                    </td>
                                    <td
                                      className="text-center"
                                      style={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}
                                    >
                                      <div className="btn-group" role="group">
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-success"
                                          onClick={() => addTermRowAfter(index)}
                                          style={{ padding: '3px 8px' }}
                                        >
                                          <i className="fa-solid fa-plus" aria-hidden="true" />
                                        </button>
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-danger"
                                          onClick={() => removeTermRow(index)}
                                          disabled={terms.length <= 1}
                                          style={{ padding: '3px 8px' }}
                                        >
                                          <i className="fa-solid fa-trash-can" aria-hidden="true" />
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}

                    {activeTab === 'materials' && (
                      <div>
                        <div className="row align-items-end mb-3">
                          <div className="col-md-6">
                            <div className="form-group mb-0">
                              <label>Search by PR Number</label>
                              <div className="input-group">
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Enter PR number..."
                                  value={prNoSearch}
                                  onChange={handlePrSearchChange}
                                  onKeyPress={handlePrSearchKeyPress}
                                />
                                <div className="input-group-append">
                                  <button
                                    type="button"
                                    className="btn btn-blue"
                                    onClick={handlePrNoSearch}
                                    disabled={prSearchLoading}
                                  >
                                    {prSearchLoading ? (
                                      <PulseLoader color="#fff" size={6} />
                                    ) : (
                                      <i className="fa-solid fa-search" />
                                    )}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {prSearchResults.length > 0 && (
                          <div
                            className="mb-3"
                            style={{
                              border: '1px solid #dee2e6',
                              borderRadius: '6px',
                              overflow: 'hidden'
                            }}
                          >
                            <div
                              className="d-flex justify-content-between align-items-center flex-wrap"
                              style={{
                                padding: '8px 12px',
                                backgroundColor: '#f8f9fa',
                                gap: '8px',
                                cursor: 'pointer'
                              }}
                              onClick={() => setPrSearchResultsOpen((open) => !open)}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault()
                                  setPrSearchResultsOpen((open) => !open)
                                }
                              }}
                            >
                              <span
                                className="d-flex align-items-center"
                                style={{ fontWeight: 600, fontSize: '13px', gap: '8px' }}
                              >
                                <i
                                  className={`fa-solid ${
                                    prSearchResultsOpen ? 'fa-chevron-up' : 'fa-chevron-down'
                                  }`}
                                  style={{ width: '14px', color: '#003366' }}
                                  aria-hidden
                                />
                                PR search results — {countPendingPrSelection()} to add,{' '}
                                {countAddedInCurrentResults()} already added
                              </span>
                              {prSearchResultsOpen && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-blue"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    applySelectedProcurements()
                                  }}
                                  disabled={countPendingPrSelection() === 0}
                                >
                                  Add
                                </button>
                              )}
                            </div>
                            {prSearchResultsOpen && (
                            <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
                              <table className="table table-sm table-bordered mb-0">
                                <thead>
                                  <tr style={{ backgroundColor: '#003366', color: '#fff' }}>
                                    <th style={{ fontSize: '11px', width: '36px' }}>
                                      <input
                                        type="checkbox"
                                        checked={
                                          getSelectablePrKeys().length > 0 &&
                                          getSelectablePrKeys().every((key) =>
                                            selectedPrKeys.has(key)
                                          )
                                        }
                                        onChange={toggleSelectAllPrResults}
                                        title="Select all not yet added"
                                      />
                                    </th>
                                    <th style={{ fontSize: '11px' }}>PR No</th>
                                    <th style={{ fontSize: '11px' }}>Material Code</th>
                                    <th style={{ fontSize: '11px' }}>Material Description</th>
                                    <th style={{ fontSize: '11px' }}>HSN</th>
                                    <th style={{ fontSize: '11px' }}>UOM</th>
                                    <th style={{ fontSize: '11px' }}>Quantity</th>
                                    <th style={{ fontSize: '11px', minWidth: '140px' }}>
                                      Quotation Status
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {prSearchResults.flatMap((item, idx) => {
                                    const rowKey = getProcurementKey(item, idx)
                                    const isAdded = isProcurementInMaterials(materials, item)
                                    const isFrozen = isProcurementFrozen(item)
                                    const isSelected = selectedPrKeys.has(rowKey)
                                    const quotationStatus = getProcurementQuotationStatus(item)
                                    const lineNo = item.lineNo || idx + 1
                                    const parentRow = (
                                    <tr
                                      key={rowKey}
                                      style={{
                                        backgroundColor: isAdded
                                          ? '#e8f5e9'
                                          : isFrozen
                                            ? '#f5f5f5'
                                            : isSelected
                                              ? '#e8f4f8'
                                              : undefined,
                                        opacity: isFrozen ? 0.75 : 1
                                      }}
                                    >
                                      <td className="text-center">
                                        <input
                                          type="checkbox"
                                          checked={isSelected}
                                          disabled={isAdded || isFrozen}
                                          onChange={() => togglePrSelection(rowKey)}
                                          title={
                                            isAdded
                                              ? 'Already added to materials'
                                              : isFrozen
                                                ? 'Only Pending lines can be selected'
                                                : 'Select parent line'
                                          }
                                        />
                                      </td>
                                      <td style={{ fontSize: '12px' }}>
                                        {item.prNo || '-'}
                                        {item.isBoq ? (
                                          <span
                                            className="badge badge-warning ml-1"
                                            style={{ fontSize: '9px' }}
                                          >
                                            BOQ
                                          </span>
                                        ) : null}
                                      </td>
                                      <td style={{ fontSize: '12px' }}>{item.materialCode || '-'}</td>
                                      <td
                                        style={{
                                          fontSize: '12px',
                                          maxWidth: '180px',
                                          whiteSpace: 'normal',
                                          wordBreak: 'break-word'
                                        }}
                                      >
                                        {item.materialDescription || '-'}
                                      </td>
                                      <td style={{ fontSize: '12px' }}>
                                        {item.hsnCode || item.hsn || '-'}
                                      </td>
                                      <td style={{ fontSize: '12px' }}>{item.uom || '-'}</td>
                                      <td style={{ fontSize: '12px' }}>
                                        {item.quantity ?? '-'}
                                        {isAdded && (
                                          <span
                                            className="badge badge-success ml-1"
                                            style={{ fontSize: '10px' }}
                                          >
                                            Added
                                          </span>
                                        )}
                                      </td>
                                      <td style={{ fontSize: '12px' }}>
                                        {quotationStatus || '-'}
                                        {isFrozen && (
                                          <span
                                            className="badge badge-secondary ml-1"
                                            style={{ fontSize: '10px' }}
                                          >
                                            Locked
                                          </span>
                                        )}
                                      </td>
                                    </tr>
                                    )

                                    const boqRows =
                                      item.isBoq && Array.isArray(item.boq)
                                        ? item.boq.map((boqItem, boqIndex) => {
                                            const boqNo =
                                              boqItem.boqNo ||
                                              `${lineNo}.${boqIndex + 1}`
                                            const boqKey = getBoqSelectionKey(
                                              rowKey,
                                              boqItem,
                                              boqIndex
                                            )
                                            const boqFrozen = isBoqItemFrozen(boqItem)
                                            const boqAdded = isBoqItemInMaterials(
                                              materials,
                                              item,
                                              boqItem
                                            )
                                            const boqSelected = selectedPrKeys.has(boqKey)
                                            const boqStatus =
                                              getProcurementQuotationStatus(boqItem)
                                            return (
                                              <tr
                                                key={boqKey}
                                                style={{
                                                  backgroundColor: boqAdded
                                                    ? '#e8f5e9'
                                                    : boqSelected
                                                      ? '#fff3cd'
                                                      : '#fff8e6',
                                                  opacity: boqFrozen ? 0.75 : 1
                                                }}
                                              >
                                                <td className="text-center">
                                                  <input
                                                    type="checkbox"
                                                    checked={boqSelected}
                                                    disabled={boqAdded || boqFrozen}
                                                    onChange={() =>
                                                      togglePrSelection(boqKey)
                                                    }
                                                    title={
                                                      boqAdded
                                                        ? 'Already added'
                                                        : boqFrozen
                                                          ? 'Only Pending BOQ lines can be selected'
                                                          : `Select BOQ ${boqNo}`
                                                    }
                                                  />
                                                </td>
                                                <td
                                                  style={{
                                                    fontSize: '12px',
                                                    fontWeight: 600,
                                                    color: '#856404',
                                                    paddingLeft: '12px'
                                                  }}
                                                >
                                                  {boqNo}
                                                </td>
                                                <td style={{ fontSize: '12px' }}>
                                                  {boqItem.materialCode || '-'}
                                                </td>
                                                <td
                                                  style={{
                                                    fontSize: '12px',
                                                    maxWidth: '180px',
                                                    whiteSpace: 'normal',
                                                    wordBreak: 'break-word'
                                                  }}
                                                >
                                                  {boqItem.materialDescription || '-'}
                                                </td>
                                                <td style={{ fontSize: '12px' }}>
                                                  {boqItem.hsnCode || boqItem.hsn || '-'}
                                                </td>
                                                <td style={{ fontSize: '12px' }}>
                                                  {boqItem.uom || '-'}
                                                </td>
                                                <td style={{ fontSize: '12px' }}>
                                                  {boqItem.quantity ?? '-'}
                                                  {boqAdded && (
                                                    <span
                                                      className="badge badge-success ml-1"
                                                      style={{ fontSize: '10px' }}
                                                    >
                                                      Added
                                                    </span>
                                                  )}
                                                </td>
                                                <td style={{ fontSize: '12px' }}>
                                                  {boqStatus || '-'}
                                                  {boqFrozen && (
                                                    <span
                                                      className="badge badge-secondary ml-1"
                                                      style={{ fontSize: '10px' }}
                                                    >
                                                      Locked
                                                    </span>
                                                  )}
                                                </td>
                                              </tr>
                                            )
                                          })
                                        : []

                                    return [parentRow, ...boqRows]
                                  })}
                                </tbody>
                              </table>
                            </div>
                            )}
                          </div>
                        )}

                        <div style={{ overflowX: "auto" }}>
                            <table className="table table-bordered table-hover mb-3">
                              <thead>
                                <tr
                                  style={{
                                    backgroundColor: "#003366",
                                    color: "#fff",
                                  }}
                                >
                                  <th style={{ fontSize: "12px" }}>S.No</th>
                                  <th style={{ fontSize: "12px" }}>
                                    PR Number
                                  </th>
                                  <th style={{ fontSize: "12px" }}>
                                    Material Code
                                  </th>
                                  <th
                                    style={{
                                      fontSize: "12px",
                                      minWidth: "220px",
                                    }}
                                  >
                                    Material Description
                                  </th>
                                  <th style={{ fontSize: "12px" }}>HSN</th>
                                  <th style={{ fontSize: "12px" }}>UOM</th>
                                  <th style={{ fontSize: "12px" }}>Quantity</th>
                                  <th
                                    style={{ fontSize: "12px", width: "90px" }}
                                  >
                                    Action
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {materials.flatMap((row, index) => {
                                  const lineNo = row.lineNo || index + 1
                                  const parentRow = (
                                  <tr
                                    key={`mat-${index}`}
                                    onClick={() =>
                                      setActiveMaterialRowIndex(index)
                                    }
                                    style={{
                                      cursor: "pointer",
                                      backgroundColor:
                                        activeMaterialRowIndex === index
                                          ? "#e8f4f8"
                                          : undefined,
                                    }}
                                  >
                                    <td style={{ fontWeight: 600 }}>
                                      {lineNo}
                                      {row.isBoq ? (
                                        <span
                                          className="badge badge-warning ml-1"
                                          style={{ fontSize: "9px" }}
                                        >
                                          BOQ
                                        </span>
                                      ) : null}
                                    </td>
                                    <td>
                                      <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        value={row.prNumber || ""}
                                        onChange={(e) =>
                                          updateMaterial(
                                            index,
                                            "prNumber",
                                            e.target.value,
                                          )
                                        }
                                        onClick={(e) => e.stopPropagation()}
                                        placeholder="PR No"
                                      />
                                    </td>
                                    <td>
                                      <div
                                        style={{
                                          position: "relative",
                                          minWidth: "160px",
                                        }}
                                      >
                                        <input
                                          ref={(el) =>
                                            (materialInputRefs.current[index] =
                                              el)
                                          }
                                          type="text"
                                          className="form-control form-control-sm"
                                          placeholder={
                                            isMaterialsCatalogLoading
                                              ? "Loading materials..."
                                              : "Search material..."
                                          }
                                          value={
                                            materialSearchTerms[index] ??
                                            row.materialCode
                                          }
                                          onChange={(e) =>
                                            handleMaterialSearchChange(
                                              index,
                                              e.target.value,
                                            )
                                          }
                                          onKeyDown={(e) =>
                                            handleMaterialKeyDown(index, e)
                                          }
                                          onFocus={() =>
                                            openMaterialDropdown(index)
                                          }
                                          onBlur={() =>
                                            setTimeout(
                                              () =>
                                                closeMaterialDropdown(index),
                                              200,
                                            )
                                          }
                                          onClick={(e) => e.stopPropagation()}
                                          autoComplete="off"
                                          disabled={isMaterialsCatalogLoading}
                                        
                                        />
                                        {isMaterialsCatalogLoading && (
                                          <div
                                            style={{
                                              position: "absolute",
                                              right: "8px",
                                              top: "50%",
                                              transform: "translateY(-50%)",
                                              pointerEvents: "none",
                                            }}
                                          >
                                            <PulseLoader
                                              color="#1199B2"
                                              size={4}
                                            />
                                          </div>
                                        )}
                                      </div>
                                    </td>
                                    <td style={{ verticalAlign: "top", minWidth: "220px" }}>
                                          <textarea
                                            ref={(el) => (materialDescInputRefs.current[index] = el)}
                                              className="form-control form-control-sm"
                                              rows={2}
                                             placeholder={
                                             isMaterialsCatalogLoading ? "Loading materials..." : "Search material description..."
                                                }
                                             value={row.materialDescription}
                                             onChange={(e) => handleMaterialDescSearchChange(index, e.target.value)}
                                             onKeyDown={(e) => handleMaterialDescKeyDown(index, e)}
                                             onFocus={() => openMaterialDescDropdown(index)}
                                             onBlur={() => setTimeout(() => closeMaterialDescDropdown(index), 200)}
                                             onClick={(e) => e.stopPropagation()}
                                             disabled={isMaterialsCatalogLoading}
                                             style={{
                                                   width: "100%",
                                                   minWidth: "200px",
                                                   resize: "vertical",
                                                   whiteSpace: "pre-wrap",
                                                  wordBreak: "break-word",
                                                  }}
                                                  />
                                     </td>
                                    <td>
                                      <input
                                          type="text"
                                        className="form-control form-control-sm"
                                        value={row.hsn}
                                        onChange={(e) =>
                                          updateMaterial(
                                            index,
                                            "hsn",
                                            e.target.value,
                                          )
                                        }
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                    </td>
                                    <td>
                                      <select
                                        className="form-control form-control-sm"
                                        value={row.uom || ''}
                                        onChange={(e) =>
                                          updateMaterial(
                                            index,
                                            'uom',
                                            e.target.value
                                          )
                                        }
                                        onClick={(e) => e.stopPropagation()}
                                        disabled={isLoading || isLoadingUnits}
                                      >
                                        <option value="">
                                          {isLoadingUnits ? 'Loading...' : 'UOM'}
                                        </option>
                                        {row.uom &&
                                          !unitOptions.some(
                                            (opt) =>
                                              opt.unit.toLowerCase() ===
                                              String(row.uom).toLowerCase()
                                          ) && (
                                            <option value={row.uom}>
                                              {row.uom}
                                            </option>
                                          )}
                                        {unitOptions.map((opt) => (
                                          <option
                                            key={opt.id || opt.unit}
                                            value={opt.unit}
                                          >
                                            {opt.unit}
                                          </option>
                                        ))}
                                      </select>
                                    </td>
                                    <td>
                                      <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        value={row.quantity}
                                        onChange={(e) =>
                                          handleQuantityChange(
                                            index,
                                            e.target.value,
                                          )
                                        }
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                    </td>
                                    <td
                                      className="text-center"
                                      style={{ whiteSpace: "nowrap" }}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <div className="btn-group" role="group">
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-success"
                                          onClick={() =>
                                            addMaterialRowAfter(index)
                                          }
                                          title="Add material row"
                                          style={{ padding: "3px 8px" }}
                                        >
                                          <i
                                            className="fa-solid fa-plus"
                                            aria-hidden="true"
                                          />
                                        </button>
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-warning"
                                          onClick={() =>
                                            addBoqUnderMaterial(index)
                                          }
                                          disabled={isLoading}
                                          title="Add BOQ line under this material"
                                          style={{ padding: "3px 8px" }}
                                        >
                                          <i
                                            className="fa-solid fa-list-ol"
                                            aria-hidden="true"
                                          />
                                        </button>
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-danger"
                                          onClick={() =>
                                            handleMaterialDeleteClick(index)
                                          }
                                          disabled={
                                            !canRemoveMaterialRow(index) ||
                                            isDeletingMaterial ||
                                            isLoading
                                          }
                                          title={
                                            row.isBoq &&
                                            Array.isArray(row.boq) &&
                                            row.boq.length > 0
                                              ? "Delete parent and all BOQ child lines"
                                              : "Delete row"
                                          }
                                          style={{ padding: "3px 8px" }}
                                        >
                                          <i
                                            className="fa-solid fa-trash-can"
                                            aria-hidden="true"
                                          />
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                  )

                                  const boqRows =
                                    row.isBoq && Array.isArray(row.boq)
                                      ? row.boq.map((boqItem, boqIndex) => {
                                          const boqNo =
                                            boqItem.boqNo ||
                                            `${lineNo}.${boqIndex + 1}`
                                          return (
                                            <tr
                                              key={`mat-${index}-boq-${boqItem._id || boqItem.boq_id || boqIndex}`}
                                              style={{
                                                backgroundColor: "#fff8e6",
                                              }}
                                            >
                                              <td
                                                style={{
                                                  fontWeight: 600,
                                                  color: "#856404",
                                                  paddingLeft: "14px",
                                                }}
                                              >
                                                {boqNo}
                                              </td>
                                              <td>
                                                <input
                                                  type="text"
                                                  className="form-control form-control-sm"
                                                  value={row.prNumber || ""}
                                                  readOnly
                                                  style={{
                                                    backgroundColor: "#f5f5f5",
                                                  }}
                                                />
                                              </td>
                                              <td>
                                                <input
                                                  type="text"
                                                  className="form-control form-control-sm"
                                                  value={
                                                    boqItem.materialCode || ""
                                                  }
                                                  onChange={(e) =>
                                                    updateBoqField(
                                                      index,
                                                      boqIndex,
                                                      "materialCode",
                                                      e.target.value
                                                    )
                                                  }
                                                  onClick={(e) =>
                                                    e.stopPropagation()
                                                  }
                                                  placeholder="Material code"
                                                />
                                              </td>
                                              <td
                                                style={{
                                                  verticalAlign: "top",
                                                  minWidth: "220px",
                                                }}
                                              >
                                                <textarea
                                                  className="form-control form-control-sm"
                                                  rows={2}
                                                  value={
                                                    boqItem.materialDescription ||
                                                    ""
                                                  }
                                                  onChange={(e) =>
                                                    updateBoqField(
                                                      index,
                                                      boqIndex,
                                                      "materialDescription",
                                                      e.target.value
                                                    )
                                                  }
                                                  onClick={(e) =>
                                                    e.stopPropagation()
                                                  }
                                                  placeholder="Material description"
                                                  style={{
                                                    width: "100%",
                                                    minWidth: "200px",
                                                    resize: "vertical",
                                                    whiteSpace: "pre-wrap",
                                                    wordBreak: "break-word",
                                                  }}
                                                />
                                              </td>
                                              <td>
                                                <input
                                                  type="text"
                                                  className="form-control form-control-sm"
                                                  value={
                                                    boqItem.hsnCode || ""
                                                  }
                                                  onChange={(e) =>
                                                    updateBoqField(
                                                      index,
                                                      boqIndex,
                                                      "hsnCode",
                                                      e.target.value
                                                    )
                                                  }
                                                  onClick={(e) =>
                                                    e.stopPropagation()
                                                  }
                                                  placeholder="HSN"
                                                />
                                              </td>
                                              <td>
                                                <select
                                                  className="form-control form-control-sm"
                                                  value={boqItem.uom || ""}
                                                  onChange={(e) =>
                                                    updateBoqField(
                                                      index,
                                                      boqIndex,
                                                      "uom",
                                                      e.target.value
                                                    )
                                                  }
                                                  onClick={(e) =>
                                                    e.stopPropagation()
                                                  }
                                                  disabled={
                                                    isLoading || isLoadingUnits
                                                  }
                                                >
                                                  <option value="">
                                                    {isLoadingUnits
                                                      ? "Loading..."
                                                      : "UOM"}
                                                  </option>
                                                  {boqItem.uom &&
                                                    !unitOptions.some(
                                                      (opt) =>
                                                        opt.unit.toLowerCase() ===
                                                        String(
                                                          boqItem.uom
                                                        ).toLowerCase()
                                                    ) && (
                                                      <option
                                                        value={boqItem.uom}
                                                      >
                                                        {boqItem.uom}
                                                      </option>
                                                    )}
                                                  {unitOptions.map((opt) => (
                                                    <option
                                                      key={
                                                        opt.id || opt.unit
                                                      }
                                                      value={opt.unit}
                                                    >
                                                      {opt.unit}
                                                    </option>
                                                  ))}
                                                </select>
                                              </td>
                                              <td>
                                                <input
                                                  type="text"
                                                  className="form-control form-control-sm"
                                                  value={
                                                    boqItem.quantity || ""
                                                  }
                                                  onChange={(e) =>
                                                    handleBoqQuantityChange(
                                                      index,
                                                      boqIndex,
                                                      e.target.value
                                                    )
                                                  }
                                                  onClick={(e) =>
                                                    e.stopPropagation()
                                                  }
                                                  placeholder="Qty"
                                                />
                                              </td>
                                              <td
                                                className="text-center"
                                                style={{ whiteSpace: "nowrap" }}
                                                onClick={(e) =>
                                                  e.stopPropagation()
                                                }
                                              >
                                                <button
                                                  type="button"
                                                  className="btn btn-sm btn-danger"
                                                  onClick={() =>
                                                    removeBoqUnderMaterial(
                                                      index,
                                                      boqIndex
                                                    )
                                                  }
                                                  disabled={isLoading}
                                                  title="Remove BOQ line"
                                                  style={{ padding: "3px 8px" }}
                                                >
                                                  <i
                                                    className="fa-solid fa-trash-can"
                                                    aria-hidden="true"
                                                  />
                                                </button>
                                              </td>
                                            </tr>
                                          )
                                        })
                                      : []

                                  return [parentRow, ...boqRows]
                                })}
                              </tbody>
                            </table>
                          </div>
                          {materials.map((row, index) => {
                            if (!showMaterialDropdown[index]) return null;
                            const pos = materialDropdownPos[index];
                            if (!pos) return null;

                            const filtered = getFilteredMaterialCatalog(index);

                            return createPortal(
                              <div
                                key={`material-dd-${index}`}
                                style={{
                                  position: "fixed",
                                  top: pos.top,
                                  left: pos.left,
                                  width: pos.width,
                                  maxHeight: "240px",
                                  overflowY: "auto",
                                  backgroundColor: "#fff",
                                  border: "1px solid #ced4da",
                                  borderRadius: "4px",
                                  zIndex: 20000,
                                  boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
                                }}
                              >
                                {filtered.length > 0
                                  ? filtered.map((material, mi) => (
                                      <div
                                        key={material._id}
                                        onMouseDown={(e) => {
                                          e.preventDefault();
                                          handleMaterialSelect(index, material);
                                        }}
                                        onMouseEnter={() =>
                                          setSelectedMaterialIndex((prev) => ({
                                            ...prev,
                                            [index]: mi,
                                          }))
                                        }
                                        style={{
                                          padding: "8px 12px",
                                          cursor: "pointer",
                                          borderBottom: "1px solid #f0f0f0",
                                          backgroundColor:
                                            mi ===
                                            (selectedMaterialIndex[index] ?? -1)
                                              ? "#e9ecef"
                                              : "#fff",
                                        }}
                                      >
                                        <strong style={{ fontSize: "13px" }}>
                                          {material.materialCode}
                                        </strong>
                                      </div>
                                    ))
                                  : materialSearchTerms[index] && (
                                      <div
                                        style={{
                                          padding: "8px 12px",
                                          color: "#6c757d",
                                          fontSize: "13px",
                                        }}
                                      >
                                        No materials found
                                      </div>
                                    )}
                              </div>,
                              document.body,
                            );
                          })}
                          {materials.map((row, index) => {
                            if (!showMaterialDescDropdown[index]) return null;
                            const pos = materialDescDropdownPos[index];
                            if (!pos) return null;

                            const filtered =
                              getFilteredMaterialDescCatalog(index);

                            return createPortal(
                              <div
                                key={`material-desc-dd-${index}`}
                                style={{
                                  position: "fixed",
                                  top: pos.top,
                                  left: pos.left,
                                  width: pos.width,
                                  maxHeight: "250px",
                                  overflowY: "auto",
                                  backgroundColor: "#fff",
                                  border: "1px solid #ced4da",
                                  borderRadius: "4px",
                                  zIndex: 20000,
                                  boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
                                }}
                              >
                                {filtered.length > 0 ? (
                                  filtered.map((material, mi) => (
                                    <div
                                      key={material._id}
                                      onMouseDown={(e) => {
                                        e.preventDefault();
                                        handleMaterialDescSelect(
                                          index,
                                          material,
                                        );
                                      }}
                                      onMouseEnter={() =>
                                        setSelectedMaterialDescIndex(
                                          (prev) => ({ ...prev, [index]: mi }),
                                        )
                                      }
                                      style={{
                                        padding: "8px 12px",
                                        cursor: "pointer",
                                        borderBottom: "1px solid #f0f0f0",
                                        backgroundColor:
                                          mi ===
                                          (selectedMaterialDescIndex[index] ??
                                            -1)
                                            ? "#e9ecef"
                                            : "#fff",
                                      }}
                                    >
                                      <strong style={{ fontSize: "13px" }}>
                                        {material.productName}
                                      </strong>
                                    </div>
                                  ))
                                ) : (
                                  <div
                                    style={{
                                      padding: "8px 12px",
                                      color: "#6c757d",
                                      fontSize: "13px",
                                    }}
                                  >
                                    No materials found
                                  </div>
                                )}
                              </div>,
                              document.body,
                            );
                          })}
                      </div>
                    )}

                    {activeTab === 'attachments' && (
                      <div>
                        <div className="row mb-3">
                          <div className="col-md-8">
                            <p className="text-muted mb-2" style={{ fontSize: '13px' }}>
                              Files are stored securely and can be downloaded later.
                            </p>
                            <input
                              ref={attachmentInputRef}
                              type="file"
                              multiple
                              style={{ display: 'none' }}
                              onChange={handleAttachmentFileSelect}
                              disabled={isLoading || isUploadingAttachments}
                            />
                            <button
                              type="button"
                              className="btn btn-blue"
                              onClick={() => attachmentInputRef.current?.click()}
                              disabled={
                                isLoading ||
                                isUploadingAttachments ||
                                rfqOwnerAttachments.length + pendingAttachmentFiles.length >=
                                  MAX_RFQ_OWNER_ATTACHMENTS
                              }
                            >
                              <i className="fa-solid fa-paperclip mr-1" />
                              Choose Files
                            </button>
                            <span className="text-muted ml-2" style={{ fontSize: '12px' }}>
                              {rfqOwnerAttachments.length + pendingAttachmentFiles.length} /{' '}
                              {MAX_RFQ_OWNER_ATTACHMENTS} files
                            </span>
                          </div>
                        </div>

                        {isUploadingAttachments && (
                          <div className="mb-3 text-muted" style={{ fontSize: '13px' }}>
                            <span className="mr-2">
                              <PulseLoader color="#1199B2" size={6} />
                            </span>
                            Uploading attachments...
                          </div>
                        )}

                        {rfqOwnerAttachments.length > 0 && (
                          <div className="mb-4">
                            <div
                              className="fwt_600 mb-2"
                              style={{ fontSize: '14px', color: '#003366' }}
                            >
                              Uploaded attachments
                            </div>
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px'
                              }}
                            >
                              {rfqOwnerAttachments.map((att) => (
                                <div
                                  key={att.s3Key}
                                  className="d-flex justify-content-between align-items-center p-2"
                                  style={{
                                    border: '1px solid #dee2e6',
                                    borderRadius: '6px',
                                    background: '#f8f9fa',
                                    gap: '10px'
                                  }}
                                >
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div
                                      style={{
                                        fontSize: '13px',
                                        fontWeight: 600,
                                        wordBreak: 'break-word'
                                      }}
                                    >
                                      <i className="fa-solid fa-file mr-1 text-secondary" />
                                      {att.originalFileName || att.s3Key}
                                    </div>
                                    {att.uploadedAt && (
                                      <div className="text-muted" style={{ fontSize: '11px' }}>
                                        Uploaded{' '}
                                        {new Date(att.uploadedAt).toLocaleString()}
                                      </div>
                                    )}
                                  </div>
                                  <div className="d-flex" style={{ gap: '8px', flexShrink: 0 }}>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-primary"
                                      onClick={() => downloadRfqOwnerAttachment(att.s3Key)}
                                      disabled={isUploadingAttachments}
                                    >
                                      <i className="fa-solid fa-download mr-1" />
                                      Download
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() => removeRfqOwnerAttachment(att.s3Key)}
                                      disabled={isUploadingAttachments || isRfqActive}
                                    >
                                      <i className="fa-solid fa-trash-can" />
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {pendingAttachmentFiles.length > 0 && (
                          <div>
                            <div
                              className="fwt_600 mb-2"
                              style={{ fontSize: '14px', color: '#003366' }}
                            >
                              Pending upload (saved when you click Next)
                            </div>
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px'
                              }}
                            >
                              {pendingAttachmentFiles.map((file, idx) => (
                                <div
                                  key={`${file.name}-${idx}`}
                                  className="d-flex justify-content-between align-items-center p-2"
                                  style={{
                                    border: '1px dashed #cbd5e1',
                                    borderRadius: '6px',
                                    background: '#fbfdff',
                                    gap: '10px'
                                  }}
                                >
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div
                                      style={{
                                        fontSize: '13px',
                                        wordBreak: 'break-word'
                                      }}
                                    >
                                      {file.name}
                                    </div>
                                    <div className="text-muted" style={{ fontSize: '11px' }}>
                                      {formatFileSize(file.size)}
                                      {file.type ? ` · ${file.type}` : ''}
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => removePendingAttachment(idx)}
                                    disabled={isUploadingAttachments}
                                  >
                                    Remove
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {rfqOwnerAttachments.length === 0 &&
                          pendingAttachmentFiles.length === 0 && (
                            <div
                              className="text-center text-muted py-4"
                              style={{
                                border: '1px dashed #dee2e6',
                                borderRadius: '6px',
                                fontSize: '13px'
                              }}
                            >
                              <i
                                className="fa-solid fa-cloud-arrow-up mb-2"
                                style={{ fontSize: '24px', display: 'block' }}
                              />
                              No attachments yet. Click &quot;Choose Files&quot; to add documents.
                            </div>
                          )}
                      </div>
                    )}

                    {activeTab === "supplier" && (
                        <div>
                          <div className="row align-items-end mb-3">
                            <div className="col-md-6">
                              <div className="form-group mb-0">
                                <label>Search Supplier</label>
                                <div className="input-group">
                                  <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search by supplier name or email..."
                                    value={supplierSearch}
                                    onChange={handleSupplierSearchChange}
                                    onKeyPress={handleSupplierSearchKeyPress}
                                  />
                                  <div className="input-group-append">
                                    <button
                                      type="button"
                                      className="btn btn-blue"
                                      onClick={handleSupplierSearch}
                                      disabled={supplierSearchLoading}
                                    >
                                      {supplierSearchLoading ? (
                                        <PulseLoader color="#fff" size={6} />
                                      ) : (
                                        <i className="fa-solid fa-search" />
                                      )}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {supplierSearchResults.length > 0 && (
                            <div
                              className="mb-3"
                              style={{
                                border: "1px solid #dee2e6",
                                borderRadius: "6px",
                                overflow: "hidden",
                              }}
                            >
                              <div
                                style={{
                                  padding: "8px 12px",
                                  backgroundColor: "#f8f9fa",
                                  fontWeight: 600,
                                  fontSize: "13px",
                                }}
                              >
                                Supplier search results —{" "}
                                {
                                  supplierSearchResults.filter(
                                    (s) => !isSupplierAlreadyAdded(s.email),
                                  ).length
                                }{" "}
                                to add,{" "}
                                {
                                  supplierSearchResults.filter((s) =>
                                    isSupplierAlreadyAdded(s.email),
                                  ).length
                                }{" "}
                                already added
                              </div>
                              <div
                                style={{
                                  maxHeight: "220px",
                                  overflowY: "auto",
                                }}
                              >
                                <table className="table table-sm table-bordered mb-0">
                                  <thead>
                                    <tr
                                      style={{
                                        backgroundColor: "#003366",
                                        color: "#fff",
                                      }}
                                    >
                                      <th style={{ fontSize: "11px" }}>
                                        Client Name
                                      </th>
                                      <th style={{ fontSize: "11px" }}>
                                        Email
                                      </th>
                                      <th style={{ fontSize: "11px" }}>
                                        Contact Person
                                      </th>
                                      <th style={{ fontSize: "11px" }}>
                                        Phone
                                      </th>
                                      <th
                                        style={{
                                          fontSize: "11px",
                                          width: "70px",
                                        }}
                                      />
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {supplierSearchResults.flatMap(
                                      (supplier, supplierIndex) => {
                                        const contacts =
                                          supplier.contacts &&
                                          supplier.contacts.length
                                            ? supplier.contacts
                                            : [
                                                {
                                                  contactPerson:
                                                    supplier.contactPerson,
                                                  email: supplier.email,
                                                  phonenumber:
                                                    supplier.phonenumber,
                                                },
                                              ];

                                        return contacts.map(
                                          (contact, contactIndex) => {
                                            const supplierContact = {
                                              ...supplier,
                                              contactPerson:
                                                contact.contactPerson || "",
                                              email: contact.email || "",
                                              phonenumber:
                                                contact.phonenumber || "",
                                            };

                                            const alreadyAdded =
                                              isSupplierAlreadyAdded(
                                                contact.email,
                                              );

                                            return (
                                              <tr
                                                key={`${supplier._id || supplier.id || supplierIndex}-${contactIndex}`}
                                                style={
                                                  alreadyAdded
                                                    ? {
                                                        backgroundColor:
                                                          "#f0f0f0",
                                                      }
                                                    : undefined
                                                }
                                              >
                                                <td
                                                  style={{ fontSize: "12px" }}
                                                >
                                                  {supplier.clientname || "-"}
                                                </td>

                                                <td
                                                  style={{ fontSize: "12px" }}
                                                >
                                                  {contact.email || "-"}
                                                </td>

                                                <td
                                                  style={{ fontSize: "12px" }}
                                                >
                                                  {contact.contactPerson || "-"}
                                                </td>

                                                <td
                                                  style={{ fontSize: "12px" }}
                                                >
                                                  {contact.phonenumber || "-"}
                                                </td>

                                                <td className="text-center">
                                                  <button
                                                    type="button"
                                                    className={`btn btn-sm ${
                                                      alreadyAdded
                                                        ? "btn-secondary"
                                                        : "btn-blue"
                                                    }`}
                                                    onClick={() =>
                                                      selectSupplier(
                                                        supplierContact,
                                                      )
                                                    }
                                                    disabled={
                                                      !contact.email?.trim() ||
                                                      alreadyAdded
                                                    }
                                                  >
                                                    {alreadyAdded
                                                      ? "Added"
                                                      : "Select"}
                                                  </button>
                                                </td>
                                              </tr>
                                            );
                                          },
                                        );
                                      },
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}

                          <div style={{ overflowX: "auto" }}>
                            <table className="table table-bordered table-hover mb-0">
                              <thead>
                                <tr
                                  style={{
                                    backgroundColor: "#003366",
                                    color: "#fff",
                                  }}
                                >
                                  <th style={{ fontSize: "12px" }}>S.No</th>
                                  <th style={{ fontSize: "12px" }}>
                                    Supplier Name
                                  </th>
                                  <th style={{ fontSize: "12px" }}>
                                    Supplier Mail ID
                                  </th>
                                  <th style={{ fontSize: "12px" }}>
                                    Phone Number
                                  </th>
                                  <th
                                    style={{ fontSize: "12px", width: "90px" }}
                                  >
                                    Action
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {suppliers.map((row, index) => (
                                  <tr key={index}>
                                    <td>{index + 1}</td>
                                    <td>
                                      <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        value={row.supplierName}
                                        onChange={(e) =>
                                          updateSupplier(
                                            index,
                                            "supplierName",
                                            e.target.value,
                                          )
                                        }
                                      />
                                    </td>
                                    <td>
                                      <input
                                        type="email"
                                        className="form-control form-control-sm"
                                        value={row.supplierMailId}
                                        onChange={(e) =>
                                          updateSupplier(
                                            index,
                                            "supplierMailId",
                                            e.target.value,
                                          )
                                        }
                                      />
                                    </td>
                                    <td>
                                      <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        value={row.phoneNumber || ""}
                                        onChange={(e) =>
                                          updateSupplier(
                                            index,
                                            "phoneNumber",
                                            normalizePhoneNumber(
                                              e.target.value,
                                            ),
                                          )
                                        }
                                        onPaste={(e) => {
                                          e.preventDefault();
                                          const pasted =
                                            e.clipboardData.getData("text");
                                          updateSupplier(
                                            index,
                                            "phoneNumber",
                                            normalizePhoneNumber(pasted),
                                          );
                                        }}
                                        maxLength={10}
                                        inputMode="numeric"
                                      />
                                    </td>
                                    <td
                                      className="text-center"
                                      style={{ whiteSpace: "nowrap" }}
                                    >
                                      <div className="btn-group" role="group">
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-success"
                                          onClick={() =>
                                            addSupplierRowAfter(index)
                                          }
                                          style={{ padding: "3px 8px" }}
                                        >
                                          <i
                                            className="fa-solid fa-plus"
                                            aria-hidden="true"
                                          />
                                        </button>
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-danger"
                                          onClick={() =>
                                            removeSupplierRow(index)
                                          }
                                          disabled={suppliers.length <= 1}
                                          style={{ padding: "3px 8px" }}
                                        >
                                          <i
                                            className="fa-solid fa-trash-can"
                                            aria-hidden="true"
                                          />
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                    {activeTab === 'preview' && canAccessPreview && (
                      <div>
                        <div
                          className="mb-4"
                          style={{
                            border: '1px solid #dee2e6',
                            borderRadius: '6px',
                            overflow: 'hidden'
                          }}
                        >
                          <div
                            style={{
                              padding: '10px 14px',
                              backgroundColor: '#f8f9fa',
                              fontWeight: 600,
                              fontSize: '14px'
                            }}
                          >
                            <i className="fa-solid fa-clipboard-check mr-2 text-primary" />
                            Review RFQ Data
                          </div>
                          <div className="p-3">
                            <div className="row mb-2">
                              <div className="col-md-3"><strong>RFQ Number:</strong> {rfqDetails.rfqNumber || '-'}</div>
                              <div className="col-md-3"><strong>RFQ Date:</strong> {rfqDetails.rfqDate || '-'}</div>
                              <div className="col-md-3"><strong>Due Date & Time:</strong>{' '}
                                {formatDueDateTimePreview(rfqDetails.rfqDueDate, rfqDetails.rfqDueTime)}
                              </div>
                              <div className="col-md-3"><strong>Currency:</strong> {rfqDetails.rfqCurrency || '-'}</div>
                            </div>
                            <div className="mb-3">
                              <strong>Summary:</strong> {rfqDetails.rfqSummary || '-'}
                            </div>
                            <div className="table-responsive mb-3">
                              <table className="table table-sm table-bordered mb-0">
                                <thead>
                                  <tr style={{ backgroundColor: '#003366', color: '#fff' }}>
                                    <th style={{ fontSize: '11px' }}>Material Code</th>
                                    <th style={{ fontSize: '11px' }}>Description</th>
                                    <th style={{ fontSize: '11px' }}>PR Number</th>
                                    <th style={{ fontSize: '11px' }}>HSN</th>
                                    <th style={{ fontSize: '11px' }}>UOM</th>
                                    <th style={{ fontSize: '11px' }}>Qty</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {getFilledMaterials().flatMap((row, index) => {
                                    const lineNo = row.lineNo || index + 1
                                    const parentRow = (
                                      <tr key={`preview-mat-${index}`}>
                                        <td>
                                          {row.materialCode || '-'}
                                          {row.isBoq ? (
                                            <span
                                              className="badge badge-warning ml-1"
                                              style={{ fontSize: '9px' }}
                                            >
                                              BOQ
                                            </span>
                                          ) : null}
                                        </td>
                                        <td
                                          style={{
                                            whiteSpace: 'normal',
                                            wordBreak: 'break-word',
                                            maxWidth: '280px'
                                          }}
                                        >
                                          {row.materialDescription || '-'}
                                        </td>
                                        <td>{row.prNumber || '-'}</td>
                                        <td>{row.hsn || '-'}</td>
                                        <td>{row.uom || '-'}</td>
                                        <td>{row.quantity || '-'}</td>
                                      </tr>
                                    )
                                    const boqRows =
                                      row.isBoq && Array.isArray(row.boq)
                                        ? row.boq.map((boqItem, boqIndex) => (
                                            <tr
                                              key={`preview-mat-${index}-boq-${boqItem._id || boqIndex}`}
                                              style={{ backgroundColor: '#fff8e6' }}
                                            >
                                              <td style={{ paddingLeft: '12px' }}>
                                                <span
                                                  style={{
                                                    fontWeight: 600,
                                                    color: '#856404',
                                                    marginRight: '6px'
                                                  }}
                                                >
                                                  {boqItem.boqNo ||
                                                    `${lineNo}.${boqIndex + 1}`}
                                                </span>
                                                {boqItem.materialCode || '-'}
                                              </td>
                                              <td
                                                style={{
                                                  whiteSpace: 'normal',
                                                  wordBreak: 'break-word',
                                                  maxWidth: '280px'
                                                }}
                                              >
                                                {boqItem.materialDescription || '-'}
                                              </td>
                                              <td>{row.prNumber || '-'}</td>
                                              <td>{boqItem.hsnCode || '-'}</td>
                                              <td>{boqItem.uom || '-'}</td>
                                              <td>{boqItem.quantity || '-'}</td>
                                            </tr>
                                          ))
                                        : []
                                    return [parentRow, ...boqRows]
                                  })}
                                </tbody>
                              </table>
                            </div>
                            {rfqOwnerAttachments.length > 0 && (
                              <div className="mb-3">
                                <strong>Attachments:</strong>
                                <ul className="mb-0 mt-1" style={{ fontSize: '13px' }}>
                                  {rfqOwnerAttachments.map((att) => (
                                    <li key={att.s3Key}>
                                      <button
                                        type="button"
                                        className="btn btn-link btn-sm p-0 align-baseline"
                                        onClick={() => downloadRfqOwnerAttachment(att.s3Key)}
                                      >
                                        {att.originalFileName || att.s3Key}
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            <div className="table-responsive">
                              <table className="table table-sm table-bordered mb-0">
                                <thead>
                                  <tr style={{ backgroundColor: '#003366', color: '#fff' }}>
                                    <th style={{ fontSize: '11px' }}>Supplier Name</th>
                                    <th style={{ fontSize: '11px' }}>Supplier Mail ID</th>
                                    <th style={{ fontSize: '11px' }}>Phone Number</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {getFilledSuppliers().map((row, index) => (
                                    <tr key={index}>
                                      <td>{row.supplierName || '-'}</td>
                                      <td>{row.supplierMailId || '-'}</td>
                                      <td>{row.phoneNumber || '-'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>

                        <div
                          className="mb-4"
                          style={{
                            border: '1px solid #dee2e6',
                            borderRadius: '6px',
                            overflow: 'hidden'
                          }}
                        >
                          <div
                            style={{
                              padding: '10px 14px',
                              backgroundColor: '#f8f9fa',
                              fontWeight: 600,
                              fontSize: '14px'
                            }}
                          >
                            <i className="fa-solid fa-file-pdf mr-2 text-primary" />
                            Preview PDF
                          </div>
                          <div className="p-3">
                            {rfqDetails.pdfLink ? (
                              <>
                                <div className="mb-3">
                                  <a
                                    href={rfqDetails.pdfLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-sm btn-outline-primary"
                                  >
                                    <i className="fa-solid fa-up-right-from-square mr-1" />
                                    Open PDF in new tab
                                  </a>
                                </div>
                                <div
                                  style={{
                                    border: '1px solid #dee2e6',
                                    borderRadius: '4px',
                                    overflow: 'hidden',
                                    minHeight: '420px'
                                  }}
                                >
                                  <iframe
                                    title="RFQ PDF Preview"
                                    src={rfqDetails.pdfLink}
                                    style={{ width: '100%', height: '420px', border: 'none' }}
                                  />
                                </div>
                              </>
                            ) : (
                              <p className="text-muted mb-0">
                                PDF is being generated. Go back to Supplier Email tab and click
                                Update RFQ again.
                              </p>
                            )}
                          </div>
                        </div>

                        <div
                          style={{
                            border: '1px solid #dee2e6',
                            borderRadius: '6px',
                            overflow: 'hidden'
                          }}
                        >
                          <div
                            style={{
                              padding: '10px 14px',
                              backgroundColor: '#f8f9fa',
                              fontWeight: 600,
                              fontSize: '14px'
                            }}
                          >
                            <i className="fa-solid fa-envelope mr-2 text-primary" />
                            Send RFQ Email
                          </div>
                          <div className="p-3">
                            <div className="form-group mb-2">
                              <label>To Emails (new suppliers)</label>
                              <div className="txtdsply" style={{ fontSize: '13px' }}>
                                {getPendingSuppliers()
                                  .map((s) => s.supplierMailId.trim())
                                  .join(', ') || '—'}
                              </div>
                            </div>
                            {userEmail ? (
                              <div className="form-group mb-2">
                                <label>CC Emails (your email)</label>
                                <div className="txtdsply" style={{ fontSize: '13px' }}>
                                  {userEmail}
                                </div>
                              </div>
                            ) : (
                              <div className="form-group mb-2">
                                <label>CC Emails (your email)</label>
                                <div className="txtdsply text-muted" style={{ fontSize: '13px' }}>
                                  Profile email not available — add CC addresses below
                                </div>
                              </div>
                            )}
                            <div className="form-group mb-2" style={{ position: 'relative' }}>
                              <label>Additional CC Emails</label>
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder="manager@example.com"
                                value={additionalCcEmails}
                                onChange={handleAdditionalCcChange}
                                onBlur={handleAdditionalCcBlur}
                                disabled={isSendingEmail}
                                autoComplete="off"
                              />
                              {(isSearchingCcUsers || ccUserSuggestions.length > 0) && (
                                <div
                                  style={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: 0,
                                    right: 0,
                                    zIndex: 1060,
                                    backgroundColor: '#fff',
                                    border: '1px solid #d8e0ea',
                                    borderRadius: '8px',
                                    boxShadow: '0 10px 24px rgba(15, 23, 42, 0.14)',
                                    maxHeight: '210px',
                                    overflowY: 'auto',
                                    marginTop: '2px'
                                  }}
                                >
                                  {isSearchingCcUsers ? (
                                    <div
                                      className="text-muted"
                                      style={{ padding: '10px 12px', fontSize: '13px' }}
                                    >
                                      <i className="fa-solid fa-spinner fa-spin mr-2" />
                                      Searching users...
                                    </div>
                                  ) : (
                                    (() => {
                                      const existing = new Set(
                                        buildCcEmails(userEmail, additionalCcEmails).map(
                                          (item) => item.toLowerCase()
                                        )
                                      )
                                      const visible = ccUserSuggestions.filter(
                                        (user) =>
                                          !existing.has(
                                            String(user.email).trim().toLowerCase()
                                          )
                                      )
                                      if (visible.length === 0) {
                                        return (
                                          <div
                                            className="text-muted"
                                            style={{ padding: '10px 12px', fontSize: '13px' }}
                                          >
                                            No matching users found
                                          </div>
                                        )
                                      }
                                      return visible.map((user) => (
                                        <button
                                          key={user.id || user.email}
                                          type="button"
                                          className="dropdown-item"
                                          style={{
                                            display: 'block',
                                            width: '100%',
                                            textAlign: 'left',
                                            padding: '8px 12px',
                                            whiteSpace: 'normal',
                                            cursor: 'pointer'
                                          }}
                                          onMouseDown={(e) => {
                                            e.preventDefault()
                                            handleSelectCcUser(user.email)
                                          }}
                                        >
                                          <span
                                            className="d-block fwt_600"
                                            style={{ fontSize: '13px' }}
                                          >
                                            {user.name || user.email}
                                          </span>
                                          <small className="text-muted">{user.email}</small>
                                        </button>
                                      ))
                                    })()
                                  )}
                                </div>
                              )}
                              <small className="form-text text-muted">
                              </small>
                              {buildCcEmails(userEmail, additionalCcEmails).length > 0 && (
                                <small className="form-text text-muted d-block">
                                  Will CC:{' '}
                                  {buildCcEmails(userEmail, additionalCcEmails).join(', ')}
                                </small>
                              )}
                            </div>
                            <div className="form-group mb-3">
                              <label>Already emailed (skipped)</label>
                              <div className="txtdsply text-muted" style={{ fontSize: '13px' }}>
                                {getAlreadyEmailedSuppliers()
                                  .map((s) => s.supplierMailId.trim())
                                  .join(', ') || '—'}
                              </div>
                            </div>
                            {!isReadyToSendEmail && (
                              <div className="alert alert-warning py-2 mb-3" role="alert">
                                Complete all RFQ details, materials, suppliers, wait for PDF,
                                and activate the RFQ before sending email.
                              </div>
                            )}
                            {isReadyToSendEmail && !hasPendingSuppliersToEmail() && (
                              <div className="alert alert-success py-2 mb-3" role="alert">
                                <i className="fa-solid fa-circle-check mr-1" />
                                All suppliers have already been emailed.
                              </div>
                            )}
                            {isReadyToSendEmail && hasPendingSuppliersToEmail() && isMailSent && (
                              <div className="alert alert-info py-2 mb-3" role="alert">
                                <i className="fa-solid fa-circle-info mr-1" />
                                New supplier(s) added — click Send Email to notify only them.
                              </div>
                            )}
                            <button
                              type="button"
                              className="btn btn-blue"
                              onClick={sendRfqEmail}
                              disabled={
                                isSendingEmail ||
                                !isReadyToSendEmail ||
                                !hasPendingSuppliersToEmail()
                              }
                            >
                              {isSendingEmail ? 'Sending...' : 'Send Email'}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  )}

                  <div className="card-footer d-flex justify-content-center align-items-center">
                    {activeTab === 'details' && (
                      <button
                        type="button"
                        className="btn btn-blue"
                        onClick={() => goToTab('materials')}
                        disabled={isLoading}
                      >
                        Next
                      </button>
                    )}

                    {activeTab === 'materials' && (
                      <>
                        <button
                          type="button"
                          className="btn btn-blue mr-2"
                          onClick={() => goToTab('details')}
                          disabled={isLoading}
                        >
                          Previous
                        </button>
                        <button
                          type="button"
                          className="btn btn-blue"
                          onClick={() => goToTab('attachments')}
                          disabled={isLoading}
                        >
                          Next
                        </button>
                      </>
                    )}

                    {activeTab === 'attachments' && (
                      <>
                        <button
                          type="button"
                          className="btn btn-blue mr-2"
                          onClick={() => goToTab('materials')}
                          disabled={isLoading || isUploadingAttachments}
                        >
                          Previous
                        </button>
                        <button
                          type="button"
                          className="btn btn-blue"
                          onClick={() => goToTab('supplier')}
                          disabled={isLoading || isUploadingAttachments}
                        >
                          {isUploadingAttachments ? 'Uploading...' : 'Next'}
                        </button>
                      </>
                    )}

                    {activeTab === 'supplier' && (
                      <>
                        <button
                          type="button"
                          className="btn btn-blue mr-2"
                          onClick={() => goToTab('attachments')}
                          disabled={isLoading}
                        >
                          Previous
                        </button>
                        <button
                          type="submit"
                          className="btn btn-blue mr-2"
                        disabled={
                          isLoading || isLoadingRfq || isFetchingRfqNumber || !rfqDetails.rfqNumber
                        }
                        >
                          {isLoading
                            ? 'Submitting...'
                            : isRfqActive
                              ? 'Update RFQ'
                              : rfqId
                                ? 'Activate RFQ'
                                : 'Create RFQ'}
                        </button>
                      </>
                    )}

                    {activeTab === 'preview' && (
                      <>
                        <button
                          type="button"
                          className="btn btn-blue mr-2"
                          onClick={() => goToTab('supplier')}
                          disabled={isLoading}
                        >
                          Previous
                        </button>
                        <Link to="/rfq" className="btn btn-secondary">
                          Back to List
                        </Link>
                      </>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal fade"
        id="modalDeleteMaterial"
        tabIndex={-1}
        aria-labelledby="modalDeleteMaterialLabel"
        aria-hidden="true"
        data-backdrop="static"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="modalDeleteMaterialLabel">
                Delete Material
              </h5>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
                disabled={isDeletingMaterial}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="modal-body">
              <div className="w-100 d-inline-block text-center">
                Are you sure you want to delete{' '}
                <span className="fwt_600">
                  {materialDeleteIndex != null
                    ? getMaterialLabel(materials[materialDeleteIndex])
                    : '-'}
                </span>
                ?
                {materialDeleteIndex != null &&
                materials[materialDeleteIndex]?.isBoq &&
                Array.isArray(materials[materialDeleteIndex]?.boq) &&
                materials[materialDeleteIndex].boq.length > 0 ? (
                  <>
                    <br />
                    <span className="text-muted" style={{ fontSize: '13px' }}>
                      All BOQ child lines under this parent will also be
                      removed.
                    </span>
                  </>
                ) : null}
              </div>
            </div>
            <div className="modal-footer justify-content-center">
              <button
                type="button"
                className="btn btn-secondary mr-2"
                data-dismiss="modal"
                disabled={isDeletingMaterial}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleConfirmDeleteMaterial}
                disabled={isDeletingMaterial}
              >
                {isDeletingMaterial ? 'Deleting...' : 'Yes, delete'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RfqCreate
